'use client';

import { RAIL_MENU_ROW, RAIL_MENU_ACTIVE, RAIL_MENU_LABEL } from './rail-menu-styles';

import {
  Box,
  Button,
  Center,
  CloseButton,
  Dialog,
  Drawer,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  Input,
  Link as ChakraLink,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  IconArchive,
  IconChevronDown,
  IconChevronRight,
  IconCompass,
  IconDots,
  IconDownload,
  IconEdit,
  IconFileText,
  IconFolder,
  IconFolderPlus,
  IconGauge,
  IconLayoutKanban,
  IconListCheck,
  IconInbox,
  IconPencil,
  IconPin,
  IconPinned,
  IconPuzzle,
  IconRefresh,
  IconSettings,
  IconServer,
  IconShare,
  IconShieldCheck,
  IconSparkles,
  IconUsers,
  IconBrandSlack,
  type TablerIcon,
} from '@tabler/icons-react';
import NextLink from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  type DragEvent,
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import type { SessionSummary } from '@/app/app/research-assistant/types';
import { useAuth } from '@/app/auth-provider';
import { getCreateProjectHref } from '@/components/onboarding/hybrid-onboarding-access';
import { useWorkspaceSlackThreads, useWorkspaceSlackThreadScope } from '@/hooks/useWorkspaceSlackThreads';
import { APP_RAIL_EXPANDED_WIDTH } from './app-rail-state';
import { useAppRail, useAppRailOverlay } from './AppRailContext';
import { AppRailFrame } from './AppRailFrame';
import { AppShellRailHeaderSlots } from './AppShellHeader';
import { SidePanelToggleButton } from './SidePanelToggleButton';
import {
  AppAccountMenu,
} from './Sidebar';
import { getPrimarySection } from './primary-navigation';
import { useEditorSessions } from './EditorSessions';
import { editorSessionHref } from './editor-sessions';
import { DataRailPanel } from './DataRailPanel';
import { AGENTS_RAIL_SLOT_ID } from './AgentsRailPanel';
import { Menu } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import {
  type SkillPickerItem,
  useEffectiveAgentSkills,
} from '@/components/research-assistant/SkillsPicker';
import { InlineSpinner } from '@/components/shared/PageLoader';
import { SessionShareDialog } from '@/components/research-assistant/SessionShareDialog';
import { useProject } from '@/contexts/ProjectContext';
import { visibleSlackThreads, workspaceThreadHref } from '@/lib/slack-agent/session-ui';
import { isProjectWorkspaceReady } from '@/lib/projects/setup';
import { isAiCmoBetaEnabled } from '@/lib/attune/growth/ai-cmo-feature';
import { formatCodexRelativeTime } from '@/lib/codex-workspace-format';
import {
  publishWorkspaceApprovalPendingCount,
  subscribeDeliveryApprovalsChanged,
} from '@/lib/delivery-approvals/client-events';
import { isDeliveryApprovalsEnabledClient } from '@/lib/delivery-approvals/feature-flags';
import {
  notifyResearchSessionListChanged,
  subscribeResearchSessionListChanged,
} from '@/lib/research-assistant/session-list-events';
import { getResearchSessionDisplayTitle } from '@/lib/research-assistant/session-titles';
import {
  buildThreadOrganizationView,
  createThreadFolder,
  createThreadOrganizationState,
  DEFAULT_THREAD_FOLDER_ICON,
  mergeThreadOrganizationSessions,
  moveThreadToFolder,
  setThreadFolderCollapsed,
  setThreadFolderIcon,
  togglePinnedThread,
  THREAD_FOLDER_EMOJI_OPTIONS,
  type OrganizedThreadFolder,
  type ThreadOrganizationState,
} from '@/lib/research-assistant/thread-organization';
import { CompactProjectSwitcher } from './Sidebar';
import { useMobileThreads } from './MobileThreadsContext';

type AppRailMode =
  | 'chat'
  | 'growth-program'
  | 'tasks'
  | 'objectives'
  | 'agents'
  | 'reports'
  | 'integrations'
  | 'north-star'
  | 'skills'
  | 'plugins'
  | 'mcp'
  | 'settings';
type ThreadNameDialogState =
  | { mode: 'folder' }
  | { mode: 'rename'; session: SessionSummary }
  | null;
type AppRailLatestRunSession = {
  automation_id: string;
  run_id: string;
  session_id: string;
  created_at?: string | null;
  updated_at?: string | null;
  completed_at?: string | null;
  automation_last_run_at?: string | null;
  automation_updated_at?: string | null;
};
type RenderThreadOptions = {
  draggable?: boolean;
  allowFolderMoves?: boolean;
  icon?: ReactNode;
  readOnly?: boolean;
};
type AppRailRequest = {
  id: number;
  ownerKey: string;
  controller: AbortController;
};

const APP_RAIL_BATCH_SIZE = 30;
const THREAD_FOLDER_BATCH_SIZE = 10;
const APP_RAIL_RETRY_MS = 1_500;
const APP_RAIL_MAX_RETRIES = 3;
const APPROVAL_COUNT_REFRESH_MS = 30_000;
const APPROVAL_COUNT_MIN_REFRESH_GAP_MS = 5_000;
const THREAD_DRAG_DATA_TYPE = 'application/x-attune-thread-id';
const BACKGROUND_AGENTS_FOLDER_ICON = '🤖';

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException('The request was aborted.', 'AbortError');
  }
}

function appIcon(IconComponent: TablerIcon) {
  return <IconComponent size={16} stroke={1.75} />;
}

function AppRailTooltip({
  content,
  disabled = false,
  children,
}: {
  content: string;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <Tooltip
      content={content}
      disabled={disabled}
      openDelay={120}
      positioning={{
        placement: 'right',
        strategy: 'fixed',
        gutter: 10,
        flip: true,
        slide: true,
        fitViewport: true,
        overflowPadding: 12,
      }}
      contentProps={{
        layerStyle: 'surfacePopover',
        borderRadius: '8px',
        px: '2.5',
        py: '1.5',
        fontSize: 'xs',
        fontWeight: '650',
        color: 'ui.text.primary',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Tooltip>
  );
}

function AppRailSkillDescriptionTooltip({
  description,
  children,
}: {
  description: string;
  children: ReactNode;
}) {
  return (
    <Tooltip
      content={description}
      openDelay={160}
      positioning={{
        placement: 'right',
        strategy: 'fixed',
        gutter: 10,
        flip: true,
        slide: true,
        fitViewport: true,
        overflowPadding: 12,
      }}
      contentProps={{
        layerStyle: 'surfacePopover',
        maxW: '320px',
        borderRadius: '8px',
        px: '3',
        py: '2.5',
        fontSize: 'xs',
        lineHeight: '1.5',
        color: 'ui.text.primary',
        whiteSpace: 'normal',
      }}
    >
      {children}
    </Tooltip>
  );
}

function createFolderId(): string {
  return `folder-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function downloadMarkdownFile(title: string, content: string): void {
  const blob = new Blob([content], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${title.replace(/[^a-z0-9]/gi, '-').toLowerCase() || 'research-thread'}.md`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function sortSessionsByUpdatedAt(sessions: SessionSummary[]): SessionSummary[] {
  return [...sessions].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
}

function upsertSessionInList(
  sessions: SessionSummary[],
  session: SessionSummary
): SessionSummary[] {
  return sortSessionsByUpdatedAt([session, ...sessions.filter(item => item.id !== session.id)]);
}

function removeSessionFromList(sessions: SessionSummary[], sessionId: string): SessionSummary[] {
  return sessions.filter(item => item.id !== sessionId);
}

function getBackgroundAgentRunTimestamp(entry: AppRailLatestRunSession): number {
  const timestamp =
    entry.updated_at ||
    entry.completed_at ||
    entry.created_at ||
    entry.automation_last_run_at ||
    entry.automation_updated_at ||
    '';
  const parsed = Date.parse(timestamp);
  return Number.isFinite(parsed) ? parsed : 0;
}

function modeHref(mode: AppRailMode) {
  if (mode === 'chat') return '/app/workspace?new_chat=true';
  if (mode === 'growth-program') return '/app/workspace?mode=growth-program';
  if (mode === 'tasks') return '/app/workspace?mode=tasks&tab=tasks';
  if (mode === 'objectives') return '/app/objectives';
  if (mode === 'agents') return '/app/agents';
  if (mode === 'reports') return '/app/docs';
  if (mode === 'integrations') return '/app/workspace?mode=integrations';
  if (mode === 'north-star') return '/app/north-star';
  if (mode === 'skills') return '/app/skills';
  if (mode === 'plugins') return '/app/plugins';
  if (mode === 'mcp') return '/app/mcp';
  return '/app/settings';
}

function isModeActive(pathname: string, searchParams: URLSearchParams, mode: AppRailMode) {
  if (mode === 'objectives') {
    return (
      pathname === '/app/objectives' ||
      pathname.startsWith('/app/objectives/') ||
      pathname === '/app/key-results' ||
      pathname.startsWith('/app/key-results/') ||
      pathname === '/app/plans' ||
      pathname.startsWith('/app/plans/')
    );
  }
  if (mode === 'tasks') {
    return (
      pathname === '/app/tasks' ||
      pathname.startsWith('/app/tasks/') ||
      (pathname === '/app/workspace' && searchParams.get('mode') === 'tasks')
    );
  }
  if (mode === 'agents') {
    return pathname === '/app/agents' || pathname.startsWith('/app/agents/');
  }
  if (mode === 'reports') {
    return (
      pathname === '/app/docs' ||
      pathname.startsWith('/app/docs/') ||
      pathname === '/app/reports' ||
      pathname.startsWith('/app/reports/')
    );
  }
  if (mode === 'settings') {
    return pathname === '/app/settings' || pathname.startsWith('/app/settings/');
  }
  if (mode === 'north-star') return pathname === '/app/north-star';
  if (mode === 'skills') {
    return (
      pathname === '/app/skills' ||
      (pathname === '/app/workspace' &&
        searchParams.get('mode') === 'integrations' &&
        searchParams.get('pluginTab') === 'skills')
    );
  }
  if (mode === 'plugins') {
    return (
      pathname === '/app/plugins' ||
      (pathname === '/app/workspace' &&
        searchParams.get('mode') === 'integrations' &&
        searchParams.get('pluginTab') !== 'skills' &&
        searchParams.get('integration') !== 'attune_mcp' &&
        searchParams.get('integration') !== 'custom_mcp')
    );
  }
  if (mode === 'mcp') {
    return (
      pathname === '/app/mcp' ||
      pathname === '/app/custom-mcp' ||
      (pathname === '/app/workspace' &&
        searchParams.get('mode') === 'integrations' &&
        (searchParams.get('integration') === 'attune_mcp' ||
          searchParams.get('integration') === 'custom_mcp'))
    );
  }
  if (pathname !== '/app/workspace') return false;
  const currentMode = searchParams.get('mode');
  if (mode === 'chat') {
    return (
      !currentMode &&
      !searchParams.get('session') &&
      !searchParams.get('sessionId') &&
      searchParams.get('new_project') !== 'true'
    );
  }
  return currentMode === mode;
}

function isApprovalsActive(pathname: string, searchParams: URLSearchParams): boolean {
  return (
    pathname === '/app/workspace' &&
    searchParams.get('mode') === 'tasks' &&
    searchParams.get('tab') === 'approvals'
  );
}

function AppRailBadge({ children }: { children: string }) {
  const isBeta = children.toLowerCase() === 'beta';

  return (
    <Text
      as="span"
      minW={isBeta ? undefined : '5'}
      h="5"
      px="1.5"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      borderRadius="full"
      bg={isBeta ? 'ui.surface.input' : 'status.warning.bg'}
      borderWidth="1px"
      borderColor={isBeta ? 'ui.border.subtle' : 'status.warning.border'}
      fontSize="2xs"
      fontWeight="700"
      lineHeight="1"
      color={isBeta ? 'ui.text.tertiary' : 'status.warning.fg'}
      fontVariantNumeric="tabular-nums"
    >
      {children}
    </Text>
  );
}

function AppRailAction({
  icon,
  label,
  badge,
  href,
  active,
  collapsed = false,
}: {
  icon: ReactNode;
  label: string;
  badge?: string;
  href: string;
  active?: boolean;
  collapsed?: boolean;
}) {
  const action = (
    <Button
      asChild
      variant="ghost"
      position="relative"
      transition="background-color 140ms ease, color 140ms ease"
      _motionReduce={{ transition: 'none' }}
      {...RAIL_MENU_ROW}
      {...(active ? RAIL_MENU_ACTIVE : {})}
    >
      <NextLink
        href={href}
        aria-label={collapsed ? label : undefined}
        aria-current={active ? 'page' : undefined}
      >
        <Icon boxSize="4" flexShrink={0} color={active ? 'accent.fg' : 'ui.text.tertiary'}>
          {icon}
        </Icon>
        <Text as="span" data-rail-label="" whiteSpace="nowrap">
          {label}
        </Text>
        {badge ? (
          <>
            <Box
              position="absolute"
              top="1"
              left="6"
              boxSize="2"
              borderRadius="full"
              bg="status.warning.fg"
              borderWidth="1px"
              borderColor="ui.surface.rail"
              opacity={collapsed ? 1 : 0}
              transition="opacity 120ms ease"
              _motionReduce={{ transition: 'none' }}
            />
            <Box data-rail-label="" ml="auto" display="inline-flex">
              <AppRailBadge>{badge}</AppRailBadge>
            </Box>
          </>
        ) : null}
      </NextLink>
    </Button>
  );

  return (
    <AppRailTooltip content={label} disabled={!collapsed}>
      {action}
    </AppRailTooltip>
  );
}

function AppRailSkillLauncher({
  skills,
  loading,
  error,
  enabled,
  collapsed = false,
  onSelect,
}: {
  skills: SkillPickerItem[];
  loading: boolean;
  error: string | null;
  enabled: boolean;
  collapsed?: boolean;
  onSelect: (skill: SkillPickerItem) => void;
}) {
  const [open, setOpen] = useState(false);
  const onOverlayOpenChange = useAppRailOverlay();

  return (
    <Menu.Root
      lazyMount
      unmountOnExit
      open={open}
      onOpenChange={details => {
        setOpen(details.open);
        onOverlayOpenChange(details);
      }}
      positioning={{ placement: 'right-start', gutter: 6 }}
    >
      <Menu.Trigger asChild>
        <Button
          variant="ghost"
          aria-label={collapsed ? 'Start with a Skill' : undefined}
          title={collapsed ? 'Start with a Skill' : undefined}
          disabled={!enabled}
          {...RAIL_MENU_ROW}
        >
          <Icon boxSize="4" flexShrink={0} color="accent.fg">
            {appIcon(IconSparkles)}
          </Icon>
          <Text as="span" data-rail-label="" whiteSpace="nowrap">
            Start with a Skill
          </Text>
          <Icon data-rail-label="" boxSize="3.5" ml="auto" color="ui.text.tertiary">
            {appIcon(IconChevronRight)}
          </Icon>
        </Button>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner zIndex="popover">
          <Menu.Content
            layerStyle="surfacePopover"
            w="min(308px, calc(100vw - 24px))"
            h="min(320px, calc(100dvh - 24px))"
            minH="0"
            display="flex"
            flexDirection="column"
            overflow="hidden"
            p="1"
          >
            <HStack px="2" pt="1" pb="1.5" minH="7" flexShrink={0} justify="space-between">
              <Text
                fontSize="10px"
                lineHeight="1"
                fontWeight="700"
                letterSpacing="0.08em"
                textTransform="uppercase"
                color="ui.text.tertiary"
              >
                Start with a Skill
              </Text>
              {!loading && skills.length > 0 ? (
                <Text fontSize="10px" lineHeight="1" color="ui.text.tertiary">
                  {skills.length}
                </Text>
              ) : null}
            </HStack>
            <Box flex="1" minH="0" overflowY="auto" overscrollBehavior="contain">
              {loading ? (
                <HStack h="full" minH="44px" px="2" gap="2" color="ui.text.secondary">
                  <InlineSpinner size="xs" />
                  <Text fontSize="xs">Loading Skills…</Text>
                </HStack>
              ) : skills.length > 0 ? (
                skills.map(skill => (
                  <AppRailSkillDescriptionTooltip
                    key={`${skill.skillId}:${skill.versionId}`}
                    description={skill.description}
                  >
                    <Menu.Item
                      value={`skill-${skill.skillId}`}
                      minH="48px"
                      px="2"
                      py="1.5"
                      gap="2.5"
                      alignItems="flex-start"
                      borderRadius="8px"
                      cursor="pointer"
                      _highlighted={{ bg: 'ui.surface.selected' }}
                      onSelect={() => onSelect(skill)}
                    >
                      <Icon boxSize="3.5" mt="0.5" color="accent.fg" flexShrink={0}>
                        {appIcon(IconSparkles)}
                      </Icon>
                      <Box minW={0} flex="1">
                        <Text fontSize="sm" fontWeight="600" color="ui.text.primary" truncate>
                          {skill.displayName}
                        </Text>
                        <Text mt="0.5" fontSize="xs" color="ui.text.tertiary" lineClamp={1}>
                          {skill.description}
                        </Text>
                      </Box>
                    </Menu.Item>
                  </AppRailSkillDescriptionTooltip>
                ))
              ) : (
                <Text px="2" py="3" fontSize="xs" color="ui.text.secondary">
                  {error || 'No Skills are available for this project.'}
                </Text>
              )}
            </Box>
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}

function AppRailNavGroup({
  label,
  collapsed = false,
  children,
}: {
  label: string;
  collapsed?: boolean;
  children: ReactNode;
}) {
  return (
    <Box w="full" mt="2.5">
      <Text
        data-rail-label=""
        aria-hidden={collapsed}
        px="3"
        mb="1"
        {...RAIL_MENU_LABEL}
      >
        {label}
      </Text>
      <VStack align="stretch" gap="0.5">
        {children}
      </VStack>
    </Box>
  );
}

function AppRailItem({
  icon,
  title,
  rightLabel,
  sourceHref,
  href,
  selected,
  draggable,
  pinned,
  folderId,
  onDragStart,
  onTogglePin,
  onRename,
  onArchive,
  archiveLabel = 'Archive thread',
  onExportMarkdown,
  onShare,
  onMoveToLoose,
  folderMoveTargets = [],
  onMoveToFolder,
}: {
  icon?: ReactNode;
  title: string;
  rightLabel?: string;
  sourceHref?: string;
  href: string;
  selected?: boolean;
  draggable?: boolean;
  pinned?: boolean;
  folderId?: string | null;
  onDragStart?: (event: DragEvent<HTMLDivElement>) => void;
  onTogglePin?: () => void;
  onRename?: () => void;
  onArchive?: () => void;
  archiveLabel?: string;
  onExportMarkdown?: () => void;
  onShare?: () => void;
  onMoveToLoose?: () => void;
  folderMoveTargets?: Array<{ id: string; name: string }>;
  onMoveToFolder?: (folderId: string) => void;
}) {
  const hasMoreActions = Boolean(
    sourceHref || folderId || folderMoveTargets.length > 0 || onShare || onRename || onExportMarkdown
  );
  const hasRowActions = Boolean(onTogglePin || onArchive || hasMoreActions);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const onOverlayOpenChange = useAppRailOverlay();
  return (
    <Box
      role="group"
      data-actions-open={actionsMenuOpen ? '' : undefined}
      w="full"
      textAlign="left"
      px="2"
      py="0.5"
      borderRadius="8px"
      bg={selected ? 'ui.surface.selected' : 'transparent'}
      borderWidth="0"
      draggable={draggable}
      onDragStart={onDragStart}
      userSelect={draggable ? 'none' : undefined}
      _hover={{
        bg: 'ui.surface.selected',
      }}
      _focusWithin={{ bg: 'ui.surface.selected' }}
      transition="background-color 140ms ease"
      _motionReduce={{ transition: 'none' }}
      css={{
        '&:hover [data-section="threadTimestamp"], &:focus-within [data-section="threadTimestamp"], &[data-actions-open] [data-section="threadTimestamp"]':
          hasRowActions
            ? {
                display: 'none',
                opacity: 0,
              }
            : {},
        '&:hover [data-section="threadRowActions"], &:focus-within [data-section="threadRowActions"], &[data-actions-open] [data-section="threadRowActions"]':
          {
            display: 'flex',
            opacity: 1,
            pointerEvents: 'auto',
          },
      }}
    >
      <HStack gap="1.5" align="center" minH="7">
        <Box flex="1" minW={0}>
          <HStack data-section="threadTitleArea" gap="0.5" minW={0}>
            <Box asChild flex="1" minW={0}>
              <NextLink href={href} aria-current={selected ? 'page' : undefined}>
                <HStack gap={icon ? '2.5' : '0'} align="center" minH="7" minW={0}>
                  {icon ? (
                    <Icon
                      boxSize="3.5"
                      color={selected ? 'accent.fg' : pinned ? 'accent.fg' : 'ui.text.tertiary'}
                      flexShrink={0}
                    >
                      {icon}
                    </Icon>
                  ) : null}
                  <Text
                    minW={0}
                    flex="1"
                    fontSize="0.875rem"
                    color="ui.text.primary"
                    fontWeight={selected ? '560' : '500'}
                    lineClamp={1}
                  >
                    {title}
                  </Text>
                </HStack>
              </NextLink>
            </Box>
          </HStack>
        </Box>

        {rightLabel || hasRowActions ? (
          <Box data-section="threadMetadataSlot" flexShrink={0} h="7">
            {rightLabel ? (
              <Text
                data-section="threadTimestamp"
                display={{ base: hasRowActions ? 'none' : 'flex', lg: 'flex' }}
                h="full"
                alignItems="center"
                justifyContent="flex-end"
                fontSize="0.875rem"
                color="ui.text.tertiary"
                fontVariantNumeric="tabular-nums"
                transition="opacity 120ms ease"
                _motionReduce={{ transition: 'none' }}
              >
                {rightLabel}
              </Text>
            ) : null}
            <HStack
              data-section="threadRowActions"
              display={{ base: 'flex', lg: 'none' }}
              h="7"
              gap="0.5"
              opacity={{ base: 1, lg: 0 }}
              pointerEvents={{ base: 'auto', lg: 'none' }}
              bg="transparent"
              borderRadius="8px"
              transition="opacity 120ms ease"
              _motionReduce={{ transition: 'none' }}
            >
              {onTogglePin ? (
                <AppRailTooltip content={pinned ? 'Unpin thread' : 'Pin thread'}>
                  <IconButton
                    aria-label={`${pinned ? 'Unpin' : 'Pin'} ${title}`}
                    size="2xs"
                    variant="ghost"
                    h="7"
                    w="7"
                    minW="7"
                    p="0"
                    borderRadius="8px"
                    color={pinned ? 'accent.fg' : 'ui.text.tertiary'}
                    _hover={{ bg: 'ui.surface.selected', color: 'ui.text.primary' }}
                    onClick={event => {
                      event.stopPropagation();
                      onTogglePin();
                    }}
                  >
                    <Icon boxSize="3.5" color="currentColor">
                      {appIcon(pinned ? IconPinned : IconPin)}
                    </Icon>
                  </IconButton>
                </AppRailTooltip>
              ) : null}
              {onArchive ? (
                <AppRailTooltip content={archiveLabel}>
                  <IconButton
                    aria-label={`${archiveLabel} ${title}`}
                    size="2xs"
                    variant="ghost"
                    h="7"
                    w="7"
                    minW="7"
                    p="0"
                    borderRadius="8px"
                    color="status.danger.fg"
                    _hover={{ bg: 'status.danger.bg', color: 'status.danger.fg' }}
                    onClick={event => {
                      event.stopPropagation();
                      onArchive();
                    }}
                  >
                    <Icon boxSize="3.5" color="currentColor">
                      {appIcon(IconArchive)}
                    </Icon>
                  </IconButton>
                </AppRailTooltip>
              ) : null}
              {hasMoreActions ? (
                <Menu.Root
                  onOpenChange={details => {
                    if (details.open) setActionsMenuOpen(true);
                    onOverlayOpenChange(details);
                  }}
                  onExitComplete={() => setActionsMenuOpen(false)}
                  lazyMount
                  unmountOnExit
                  positioning={{ placement: 'right-start', gutter: 6 }}
                >
                  <Menu.Trigger asChild>
                    <IconButton
                      aria-label={`More actions for ${title}`}
                      title="More thread actions"
                      size="2xs"
                      variant="ghost"
                      h="7"
                      w="7"
                      minW="7"
                      p="0"
                      borderRadius="8px"
                      color="ui.text.tertiary"
                      _hover={{ bg: 'ui.surface.selected', color: 'ui.text.primary' }}
                      onClick={event => event.stopPropagation()}
                    >
                      <Icon boxSize="3.5" color="currentColor">
                        {appIcon(IconDots)}
                      </Icon>
                    </IconButton>
                  </Menu.Trigger>
                  <Portal>
                    <Menu.Positioner zIndex="popover">
                      <Menu.Content layerStyle="surfacePopover" minW="204px" p="1">
                        {sourceHref ? (
                          <Menu.Item
                            value="open-source"
                            onSelect={() => window.open(sourceHref, '_blank', 'noopener,noreferrer')}
                            cursor="pointer"
                          >
                            <Icon boxSize="4" color="ui.text.tertiary">
                              {appIcon(IconBrandSlack)}
                            </Icon>
                            <Menu.ItemText>Open in Slack</Menu.ItemText>
                          </Menu.Item>
                        ) : null}
                        {folderId && onMoveToLoose ? (
                          <Menu.Item value="move-loose" onSelect={onMoveToLoose} cursor="pointer">
                            <Icon boxSize="4" color="ui.text.tertiary">
                              {appIcon(IconInbox)}
                            </Icon>
                            <Menu.ItemText>Move out of folder</Menu.ItemText>
                          </Menu.Item>
                        ) : null}
                        {folderMoveTargets.map(folder => (
                          <Menu.Item
                            key={folder.id}
                            value={`move-folder-${folder.id}`}
                            onSelect={() => onMoveToFolder?.(folder.id)}
                            cursor="pointer"
                          >
                            <Icon boxSize="4" color="ui.text.tertiary">
                              {appIcon(IconFolder)}
                            </Icon>
                            <Menu.ItemText>Move to {folder.name}</Menu.ItemText>
                          </Menu.Item>
                        ))}
                        {folderId || folderMoveTargets.length > 0 ? (
                          <Menu.Separator my="1" borderColor="ui.border.subtle" />
                        ) : null}
                        {onShare ? (
                          <Menu.Item value="share" onSelect={onShare} cursor="pointer">
                            <Icon boxSize="4" color="ui.text.tertiary">
                              {appIcon(IconShare)}
                            </Icon>
                            <Menu.ItemText>Share</Menu.ItemText>
                          </Menu.Item>
                        ) : null}
                        {onRename ? (
                          <Menu.Item value="rename" onSelect={onRename} cursor="pointer">
                            <Icon boxSize="4" color="ui.text.tertiary">
                              {appIcon(IconPencil)}
                            </Icon>
                            <Menu.ItemText>Rename</Menu.ItemText>
                          </Menu.Item>
                        ) : null}
                        {onExportMarkdown ? (
                          <Menu.Item
                            value="export-markdown"
                            onSelect={onExportMarkdown}
                            cursor="pointer"
                          >
                            <Icon boxSize="4" color="ui.text.tertiary">
                              {appIcon(IconDownload)}
                            </Icon>
                            <Menu.ItemText>Export Markdown</Menu.ItemText>
                          </Menu.Item>
                        ) : null}
                      </Menu.Content>
                    </Menu.Positioner>
                  </Portal>
                </Menu.Root>
              ) : null}
            </HStack>
          </Box>
        ) : null}
      </HStack>
    </Box>
  );
}

function AppRailThreadFolder({
  folder,
  active,
  onToggle,
  onDragOver,
  onDragLeave,
  onDrop,
  onIconChange,
  children,
}: {
  folder: OrganizedThreadFolder;
  active?: boolean;
  onToggle: () => void;
  onDragOver: (event: DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
  onDrop: (event: DragEvent<HTMLDivElement>) => void;
  onIconChange: (icon: string) => void;
  children: ReactNode;
}) {
  const onOverlayOpenChange = useAppRailOverlay();
  return (
    <Box
      data-section="threadFolderGroup"
      borderRadius="12px"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <HStack
        data-section="threadFolderHeader"
        h="8"
        px="2"
        gap="0"
        borderRadius="12px"
        bg={active ? 'ui.surface.selected' : 'transparent'}
        _hover={{ bg: 'ui.surface.selected' }}
      >
        <Menu.Root
          lazyMount
          unmountOnExit
          onOpenChange={onOverlayOpenChange}
          positioning={{ placement: 'right-start', gutter: 6 }}
        >
          <Menu.Trigger asChild>
            <IconButton
              aria-label={`Change folder icon for ${folder.name}`}
              title="Change folder icon"
              size="2xs"
              variant="ghost"
              h="7"
              minW="5"
              w="5"
              px="0"
              borderRadius="8px"
              color="ui.text.secondary"
              fontSize="0.875rem"
              _hover={{ bg: 'transparent', color: 'ui.text.primary' }}
            >
              {folder.icon}
            </IconButton>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner zIndex="popover">
              <Menu.Content layerStyle="surfacePopover" minW="164px" p="1">
                <Box
                  data-section="folderEmojiPicker"
                  display="grid"
                  gridTemplateColumns="repeat(6, minmax(0, 1fr))"
                  gap="1"
                >
                  {THREAD_FOLDER_EMOJI_OPTIONS.map(icon => (
                    <Menu.Item
                      key={icon}
                      value={`folder-icon-${folder.id}-${icon}`}
                      onSelect={() => onIconChange(icon)}
                      justifyContent="center"
                      borderRadius="8px"
                      bg={folder.icon === icon ? 'ui.surface.selected' : 'transparent'}
                      cursor="pointer"
                    >
                      <Menu.ItemText>{icon}</Menu.ItemText>
                    </Menu.Item>
                  ))}
                </Box>
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>

        <Button
          variant="ghost"
          flex="1"
          minW={0}
          h="7"
          px="1"
          justifyContent="space-between"
          borderRadius="8px"
          color="ui.text.secondary"
          _hover={{ bg: 'transparent', color: 'ui.text.primary' }}
          onClick={onToggle}
          aria-expanded={!folder.collapsed}
        >
          <HStack gap="1.5" minW={0}>
            <Text fontSize="0.875rem" fontWeight="600" lineClamp={1}>
              {folder.name}
            </Text>
          </HStack>
          <HStack gap="1.5" flexShrink={0}>
            <Text fontSize="0.8125rem" color="ui.text.tertiary">
              {folder.threads.length}
            </Text>
            <Icon boxSize="3" color="ui.text.tertiary" flexShrink={0}>
              {appIcon(folder.collapsed ? IconChevronRight : IconChevronDown)}
            </Icon>
          </HStack>
        </Button>
      </HStack>
      {folder.collapsed ? null : (
        <VStack
          align="stretch"
          gap="0"
          ml="3"
          pl="1"
          borderLeftWidth="1px"
          borderColor="ui.border.subtle"
        >
          {children}
        </VStack>
      )}
    </Box>
  );
}

function SharedWithMeThreadFolder({
  active,
  collapsed,
  count,
  onToggle,
  children,
}: {
  active?: boolean;
  collapsed: boolean;
  count: number;
  onToggle: () => void;
  children?: ReactNode;
}) {
  return (
    <Box data-section="sharedWithMeThreadFolder" borderRadius="12px">
      <HStack
        data-section="sharedWithMeThreadFolderHeader"
        h="8"
        px="2"
        gap="1"
        borderRadius="12px"
        bg={active ? 'ui.surface.selected' : 'transparent'}
        _hover={{ bg: 'ui.surface.selected' }}
      >
        <Button
          variant="ghost"
          flex="1"
          minW={0}
          h="7"
          px="1"
          justifyContent="space-between"
          borderRadius="8px"
          color={active ? 'ui.text.primary' : 'ui.text.secondary'}
          _hover={{ bg: 'transparent', color: 'ui.text.primary' }}
          onClick={onToggle}
          aria-expanded={!collapsed}
        >
          <HStack gap="1.5" minW={0}>
            <Icon boxSize="3.5" color="ui.text.tertiary" flexShrink={0}>
              {appIcon(IconUsers)}
            </Icon>
            <Text fontSize="0.875rem" fontWeight="600" lineClamp={1}>
              Shared with me
            </Text>
          </HStack>
          <HStack gap="1.5" flexShrink={0}>
            <Text fontSize="0.875rem" color="ui.text.tertiary">
              {count}
            </Text>
            <Icon boxSize="3" color="ui.text.tertiary" flexShrink={0}>
              {appIcon(collapsed ? IconChevronRight : IconChevronDown)}
            </Icon>
          </HStack>
        </Button>
      </HStack>
      {collapsed ? null : (
        <VStack
          align="stretch"
          gap="0"
          ml="3"
          pl="1"
          borderLeftWidth="1px"
          borderColor="ui.border.subtle"
        >
          {children}
        </VStack>
      )}
    </Box>
  );
}

function AppRailThreadListMenu({
  compact = false,
  onCreateFolder,
  onRefresh,
  onArchiveAll,
  refreshing = false,
  archivingAll = false,
  archiveAllDisabled = false,
}: {
  compact?: boolean;
  onCreateFolder: () => void;
  onRefresh: () => void;
  onArchiveAll?: () => void;
  refreshing?: boolean;
  archivingAll?: boolean;
  archiveAllDisabled?: boolean;
}) {
  return (
    <HStack
      data-section="threadListMenuHeader"
      justify="space-between"
      minH="8"
      px={compact ? '0' : '2'}
      mt={compact ? '0' : '1'}
      mb={compact ? '0' : '0.5'}
      flexShrink={0}
      color="ui.text.tertiary"
    >
      {!compact ? (
        <Text
          fontSize="0.75rem"
          fontWeight="700"
          lineHeight="1"
          color="ui.text.tertiary"
          letterSpacing="0.06em"
          textTransform="uppercase"
        >
          Threads
        </Text>
      ) : null}
      <HStack gap="0.5">
        {onArchiveAll ? (
          <AppRailTooltip content="Archive all threads">
            <Button
              size="xs"
              variant="ghost"
              h="7"
              w="7"
              minW="7"
              p="0"
              borderRadius="8px"
              color="ui.text.tertiary"
              onClick={onArchiveAll}
              disabled={archiveAllDisabled || archivingAll}
              aria-label="Archive all threads"
              _hover={{ bg: 'status.danger.bg', color: 'status.danger.fg' }}
            >
              {archivingAll ? (
                <InlineSpinner size="xs" />
              ) : (
                <Icon boxSize="3.5" color="currentColor">
                  {appIcon(IconArchive)}
                </Icon>
              )}
            </Button>
          </AppRailTooltip>
        ) : null}
        <AppRailTooltip content="Refresh threads">
          <Button
            size="xs"
            variant="ghost"
            h="7"
            w="7"
            minW="7"
            p="0"
            borderRadius="8px"
            color="ui.text.tertiary"
            onClick={onRefresh}
            disabled={refreshing}
            aria-label="Refresh left rail"
            _hover={{ bg: 'ui.surface.selected', color: 'ui.text.primary' }}
          >
            <Icon
              boxSize="3.5"
              color="currentColor"
              animation={refreshing ? 'spin 0.8s linear infinite' : undefined}
              _motionReduce={{ animation: 'none' }}
            >
              {appIcon(IconRefresh)}
            </Icon>
          </Button>
        </AppRailTooltip>
        <AppRailTooltip content="New folder" disabled={!compact}>
          <Button
            aria-label="New folder"
            size="xs"
            variant="ghost"
            h="7"
            w={compact ? '7' : undefined}
            minW="7"
            px={compact ? '0' : '2'}
            borderRadius="8px"
            color="ui.text.secondary"
            fontWeight="600"
            onClick={onCreateFolder}
            _hover={{ bg: 'ui.surface.selected', color: 'ui.text.primary' }}
          >
            <Icon boxSize="3.5" color="ui.text.tertiary">
              {appIcon(IconFolderPlus)}
            </Icon>
            {compact ? null : 'New folder'}
          </Button>
        </AppRailTooltip>
      </HStack>
    </HStack>
  );
}

function AuthPreviewProjectSwitcher() {
  return (
    <HStack
      minH="38px"
      px="3"
      borderRadius="12px"
      bg="ui.surface.input"
      gap="1.5"
      minW={0}
      overflow="hidden"
    >
      <Text
        fontSize="0.875rem"
        fontWeight="600"
        color="ui.text.primary"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        flex="1"
        minW={0}
      >
        Sign in required
      </Text>
      <Icon boxSize="3.5" color="ui.text.tertiary" flexShrink={0} aria-hidden="true">
        {appIcon(IconChevronDown)}
      </Icon>
    </HStack>
  );
}

function AuthPreviewRailAction({
  icon,
  label,
  badge,
}: {
  icon: ReactNode;
  label: string;
  badge?: string;
}) {
  return (
    <Box
      bg="transparent"
      opacity="0.78"
      cursor="default"
      aria-disabled="true"
      {...RAIL_MENU_ROW}
    >
      <Icon boxSize="4" color="ui.text.tertiary">
        {icon}
      </Icon>
      <Text as="span">{label}</Text>
      {badge ? (
        <Box ml="auto" display="inline-flex">
          <AppRailBadge>{badge}</AppRailBadge>
        </Box>
      ) : null}
    </Box>
  );
}

function AuthPreviewRail() {
  return (
    <Flex data-section="appShellRail" display={{ base: 'none', lg: 'flex' }} direction="column"
      h="full" minH={0} w={APP_RAIL_EXPANDED_WIDTH} flexShrink={0} px="3"
      borderRightWidth="1px" borderColor="ui.border.divider" bg="ui.surface.rail" overflow="hidden">
      <Flex h="58px" align="center" flexShrink={0} px="3"><Box w="full"><AuthPreviewProjectSwitcher /></Box></Flex>
      <Text px="3" pt="3" pb="2" fontSize="lg" fontWeight="600" letterSpacing="-0.025em">Chats</Text>
      <VStack align="stretch" gap="0.5" flexShrink={0} mt="2.5">
        <AuthPreviewRailAction icon={appIcon(IconEdit)} label="New chat" />
        <AuthPreviewRailAction icon={appIcon(IconSparkles)} label="Start with a Skill" />
      </VStack>
      <Box data-section="threadListScrollRegion" flex="1" minH={0} overflowY="auto" px="3" mt="6">
        <Text fontSize="xs" color="ui.text.tertiary">Your chats</Text>
        <Text mt="2" fontSize="sm" lineHeight="1.6" color="ui.text.secondary">Sign in to start a conversation and see your saved chats.</Text>
      </Box>
    </Flex>
  );
}

function OnboardingLockedRail() {
  const { projects, loading, switchProject } = useProject();
  const switchableProjects = useMemo(() => projects.filter(isProjectWorkspaceReady), [projects]);

  const selectExistingProject = useCallback(
    (projectId: string) => {
      const project = switchableProjects.find(candidate => candidate.id === projectId);
      if (!project) return;
      switchProject(projectId);
    },
    [switchProject, switchableProjects]
  );

  return (
    <Flex
      data-section="appShellRail"
      display={{ base: 'none', lg: 'flex' }}
      direction="column"
      h="full"
      minH={0}
      w={APP_RAIL_EXPANDED_WIDTH}
      flexShrink={0}
      px="3"
      py="0"
      borderRightWidth="1px"
      borderColor="ui.border.divider"
      bg="ui.surface.rail"
      overflow="hidden"
    >
      <Flex h="58px" align="center" flexShrink={0} px="3" gap="2">
        <Box flex="1" minW={0}>
          {switchableProjects.length > 0 || loading ? (
            <CompactProjectSwitcher
              placement="rail"
              currentProject={null}
              projects={switchableProjects}
              loading={loading}
              canCreateProject={false}
              onSelectProject={selectExistingProject}
              onCreateProject={() => {}}
            />
          ) : (
            <Box
              minH="38px"
              px="3"
              borderRadius="12px"
              bg="ui.surface.input"
              display="flex"
              alignItems="center"
              minW={0}
            >
              <Text fontSize="0.875rem" fontWeight="600" color="ui.text.primary" truncate>
                New project
              </Text>
            </Box>
          )}
        </Box>
      </Flex>

      <Text px="3" pt="3" pb="2" fontSize="lg" fontWeight="600" letterSpacing="-0.025em">Project setup</Text>
      <Box flex="1" minH={0} mt="5" px="3">
        <Text
          fontSize="2xs"
          fontWeight="700"
          letterSpacing="0.06em"
          textTransform="uppercase"
          color="ui.text.tertiary"
        >
          Onboarding
        </Text>
        <Text mt="2" fontSize="xs" lineHeight="1.5" color="ui.text.secondary">
          Add optional tools and data, then open your workspace whenever you’re ready.
        </Text>
      </Box>

    </Flex>
  );
}

export function AppRail({
  authPreview = false,
  initialIsAdmin = false,
}: { authPreview?: boolean; initialIsAdmin?: boolean } = {}) {
  if (authPreview) {
    return <AuthPreviewRail />;
  }

  return <AuthenticatedAppRail initialIsAdmin={initialIsAdmin} />;
}

function AuthenticatedAppRail({ initialIsAdmin }: { initialIsAdmin: boolean }) {
  const { currentProject } = useProject();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const onboardingLocked =
    pathname === '/app/workspace' &&
    (searchParams.get('new_project') === 'true' ||
      searchParams.get('setup') === 'true' ||
      (searchParams.get('onboarding_chat') === 'true' &&
        !searchParams.get('session') &&
        !searchParams.get('sessionId')));

  if (onboardingLocked) return <OnboardingLockedRail />;
  return (
    <ProjectScopedAuthenticatedAppRail
      key={currentProject?.id || 'no-project'}
      initialIsAdmin={initialIsAdmin}
    />
  );
}

function WorkspaceSlackThreadsSection({
  threads,
  scope,
  workspaceId,
  activeSessionId,
  onRename,
}: {
  threads: ReturnType<typeof useWorkspaceSlackThreads>;
  scope: ReturnType<typeof useWorkspaceSlackThreadScope>;
  workspaceId: string | null;
  activeSessionId: string | null;
  onRename: (session: SessionSummary) => void;
}) {
  const [showHidden, setShowHidden] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const preferences = new Map(
    threads.preferences.map(preference => [preference.sessionId, preference])
  );
  const hiddenCount = threads.sessions.filter(
    session => preferences.get(session.id)?.hidden
  ).length;
  const updatePreference = (sessionId: string, patch: { pinned?: boolean; hidden?: boolean }) => {
    void threads.updatePreference(sessionId, patch).catch(error => toast.error(error.message));
  };
  const visibleSessions = visibleSlackThreads(threads.sessions, threads.preferences, showHidden);
  const active = threads.sessions.some(session => session.id === activeSessionId);
  return (
    <Box data-section="workspaceSlackThreads" borderRadius="12px">
      <HStack
        h="8"
        px="2"
        gap="1"
        borderRadius="12px"
        bg={active ? 'ui.surface.selected' : 'transparent'}
        _hover={{ bg: 'ui.surface.selected' }}
      >
        <Button
          variant="ghost"
          flex="1"
          minW={0}
          h="7"
          px="1"
          justifyContent="space-between"
          borderRadius="8px"
          color={active ? 'ui.text.primary' : 'ui.text.secondary'}
          _hover={{ bg: 'transparent', color: 'ui.text.primary' }}
          onClick={() => setCollapsed(value => !value)}
          aria-expanded={!collapsed}
        >
          <HStack gap="1.5" minW={0}>
            <Icon boxSize="3.5" color="ui.text.tertiary" flexShrink={0}>
              {appIcon(IconFolder)}
            </Icon>
            <Text fontSize="0.875rem" fontWeight="600" lineClamp={1}>
              Slack
            </Text>
          </HStack>
          <HStack gap="1.5" flexShrink={0}>
            <Text fontSize="0.875rem" color="ui.text.tertiary" minW="4" textAlign="right" fontVariantNumeric="tabular-nums">
              {threads.loading && !threads.sessions.length ? '…' : visibleSessions.length}
            </Text>
            <Icon boxSize="3" color="ui.text.tertiary" flexShrink={0}>
              {appIcon(collapsed ? IconChevronRight : IconChevronDown)}
            </Icon>
          </HStack>
        </Button>
      </HStack>
      {collapsed ? null : (
        <VStack
          align="stretch"
          gap="0"
          ml="3"
          pl="1"
          borderLeftWidth="1px"
          borderColor="ui.border.subtle"
        >
          {scope.error ? (
            <Button size="xs" variant="ghost" onClick={scope.retry}>
              {scope.error} · Retry
            </Button>
          ) : null}
          {scope.workspaces.length > 1 ? (
            <select
              aria-label="Slack thread workspace"
              value={workspaceId || ''}
              onChange={event => scope.selectWorkspace(event.target.value)}
              style={{ width: '100%', maxWidth: '100%' }}
            >
              <option value="">Choose a Slack workspace</option>
              {scope.workspaces.map(workspace => (
                <option key={workspace.id} value={workspace.id}>
                  {workspace.name}
                </option>
              ))}
            </select>
          ) : null}
          <HStack px="2" justify="space-between">
            {hiddenCount ? (
              <Button size="2xs" variant="ghost" onClick={() => setShowHidden(value => !value)}>
                {showHidden ? 'Hide hidden' : `Hidden (${hiddenCount})`}
              </Button>
            ) : null}
          </HStack>
          {threads.error ? (
            <Button size="xs" variant="ghost" onClick={() => void threads.refresh()}>
              Slack threads unavailable · Retry
            </Button>
          ) : null}
          {threads.loading && !threads.sessions.length ? (
            <Text px="2" py="2" fontSize="xs" role="status">
              Loading Slack threads…
            </Text>
          ) : null}
          {workspaceId
            ? visibleSessions.map(session => {
                const preference = preferences.get(session.id);
                return (
                  <AppRailItem
                    key={session.id}
                    title={getResearchSessionDisplayTitle(session)}
                    icon={session.origin ? appIcon(IconBrandSlack) : undefined}
                    href={workspaceThreadHref(session.id, workspaceId)}
                    selected={session.id === activeSessionId}
                    sourceHref={session.origin?.deepLink || undefined}
                    rightLabel={
                      preference?.hidden ? 'Hidden' : !session.origin ? 'Private fork' : undefined
                    }
                    pinned={preference?.pinned === true}
                    draggable={false}
                    onTogglePin={() =>
                      updatePreference(session.id, { pinned: !preference?.pinned })
                    }
                    onArchive={() => updatePreference(session.id, { hidden: !preference?.hidden })}
                    archiveLabel={
                      preference?.hidden ? 'Show in my threads' : 'Hide from my threads'
                    }
                    onRename={() => onRename(session)}
                  />
                );
              })
            : null}
          {!threads.loading && !threads.error && !scope.error && !visibleSessions.length ? (
            <Text px="2" py="2" fontSize="xs" color="ui.text.tertiary">
              {workspaceId ? 'No Slack threads yet.' : 'Choose or connect a Slack workspace to see its threads.'}
            </Text>
          ) : null}
          {threads.hasMore ? (
            <Button
              size="xs"
              variant="ghost"
              loading={threads.loading}
              onClick={() => void threads.loadMore()}
            >
              Load more Slack threads
            </Button>
          ) : null}
        </VStack>
      )}
    </Box>
  );
}

function ProjectScopedAuthenticatedAppRail(_props: { initialIsAdmin: boolean }) {
  const {
    railCollapsed,
    railHovered,
    railStateReady,
    railVisuallyCollapsed,
    railToggleLabel,
    toggleRailCollapsed,
    closeRailHover,
    handleRailPointerEnter,
    handleRailPointerLeave,
  } = useAppRail();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user } = useAuth();
  const primarySection = getPrimarySection(pathname, searchParams);
  const editorHistory = useEditorSessions();
  const editorApp = primarySection === 'site' ? 'website' : 'canvas';
  const { open: mobileThreadsOpen, setOpen: setMobileThreadsOpen } = useMobileThreads();
  const {
    currentProject,
    projects,
    manageableWorkspaces,
    canCreatePersonalProject,
    loading: projectLoading,
    isReady: projectReady,
    switchProject,
  } = useProject();
  const projectId = currentProject?.id || null;
  const slackScope = useWorkspaceSlackThreadScope((searchParams.get('session_scope') === 'workspace' ? searchParams.get('workspace_id') : null) ||
    currentProject?.workspace_id || null);
  const slackWorkspaceId = slackScope.workspaceId;
  const slackThreads = useWorkspaceSlackThreads(slackWorkspaceId);
  const activeProjectIdRef = useRef<string | null>(projectId);
  activeProjectIdRef.current = projectId;
  const railSkills = useEffectiveAgentSkills(projectId);
  const railSkillLauncherEnabled = Boolean(
    projectId &&
      !projectLoading &&
      !railSkills.loading &&
      railSkills.controls.explicitInvocationEnabled
  );
  const aiCmoBetaEnabled = isAiCmoBetaEnabled(currentProject);
  const deliveryApprovalsEnabled = isDeliveryApprovalsEnabledClient();
  const navigationStateKey = `${pathname}?${searchParams.toString()}`;

  useEffect(() => {
    setMobileThreadsOpen(false);
  }, [navigationStateKey, setMobileThreadsOpen]);

  useEffect(() => {
    activeProjectIdRef.current = projectId;
    return () => {
      if (activeProjectIdRef.current === projectId) activeProjectIdRef.current = null;
    };
  }, [projectId]);

  const hasProject = projects.length > 0;
  const activeManageableWorkspace =
    manageableWorkspaces.find(workspace => workspace.id === currentProject?.workspace_id) || null;
  const fallbackManageableWorkspace = manageableWorkspaces[0] || null;
  const workspaceForCreate =
    activeManageableWorkspace || (!canCreatePersonalProject ? fallbackManageableWorkspace : null);
  const canManageCurrentWorkspace = Boolean(workspaceForCreate);
  const canCreateProject = canCreatePersonalProject || canManageCurrentWorkspace;
  const createProjectHref = getCreateProjectHref({
    hasProject,
    canCreatePersonalProject,
    manageableWorkspaces,
    requestedWorkspaceId: workspaceForCreate?.id || null,
  });
  const startNewTaskWithSkill = useCallback(
    (skill: SkillPickerItem) => {
      if (!railSkillLauncherEnabled) return;
      const params = new URLSearchParams();
      params.set('new_chat', 'true');
      params.set('composer_skill', skill.skillId);
      params.set('composer_draft', `Use ${skill.displayName} to `);
      router.push(`/app/workspace?${params.toString()}`);
    },
    [railSkillLauncherEnabled, router]
  );
  const activeSessionId =
    pathname === '/app/workspace'
      ? searchParams.get('session') || searchParams.get('sessionId')
      : null;
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [organizationSessions, setOrganizationSessions] = useState<SessionSummary[]>([]);
  const [backgroundAgentSessions, setBackgroundAgentSessions] = useState<SessionSummary[]>([]);
  const [backgroundAgentSessionIdOrder, setBackgroundAgentSessionIdOrder] = useState<string[]>([]);
  const [backgroundAgentSessionIdsLoaded, setBackgroundAgentSessionIdsLoaded] = useState(false);
  const [sharedSessions, setSharedSessions] = useState<SessionSummary[]>([]);
  const [sharedSessionsCursor, setSharedSessionsCursor] = useState<string | null>(null);
  const [sharedSessionsHasMore, setSharedSessionsHasMore] = useState(false);
  const [sharedSessionsLoadingMore, setSharedSessionsLoadingMore] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [railRefreshing, setRailRefreshing] = useState(false);
  const [archivingAllThreads, setArchivingAllThreads] = useState(false);
  const [approvalPendingCount, setApprovalPendingCount] = useState(0);
  const [visibleSessionCount, setVisibleSessionCount] = useState(APP_RAIL_BATCH_SIZE);
  const [visibleBackgroundAgentCount, setVisibleBackgroundAgentCount] =
    useState(THREAD_FOLDER_BATCH_SIZE);
  const [visibleFolderThreadCounts, setVisibleFolderThreadCounts] = useState<
    Record<string, number>
  >({});
  const [backgroundAgentsCollapsed, setBackgroundAgentsCollapsed] = useState(true);
  const [backgroundAgentSessionsLoaded, setBackgroundAgentSessionsLoaded] = useState(false);
  const [sharedWithMeCollapsed, setSharedWithMeCollapsed] = useState(false);
  const [threadOrganization, setThreadOrganization] = useState<ThreadOrganizationState>(() =>
    createThreadOrganizationState()
  );
  const [threadOrganizationReady, setThreadOrganizationReady] = useState(false);
  const [threadOrganizationProjectId, setThreadOrganizationProjectId] = useState<string | null>(
    null
  );
  const [dragTarget, setDragTarget] = useState<string | null>(null);
  const [nameDialog, setNameDialog] = useState<ThreadNameDialogState>(null);
  const [nameDialogValue, setNameDialogValue] = useState('');
  const [nameDialogFolderIcon, setNameDialogFolderIcon] = useState<string>(
    DEFAULT_THREAD_FOLDER_ICON
  );
  const [nameDialogSaving, setNameDialogSaving] = useState(false);
  const [shareDialogSession, setShareDialogSession] = useState<SessionSummary | null>(null);
  const skipOrganizationSaveRef = useRef(true);
  const requestSequenceRef = useRef(0);
  const approvalCountRequestSequenceRef = useRef(0);
  const sessionsRequestRef = useRef<AppRailRequest | null>(null);
  const sessionsRetryTimerRef = useRef<number | null>(null);
  const sessionsRetryAttemptRef = useRef(0);
  const backgroundAgentsRequestRef = useRef<AppRailRequest | null>(null);
  const threadOrganizationLoadedProjectRef = useRef<string | null>(null);
  const requestOwnerKey = user && projectId ? `${user.uid}:${projectId}` : null;
  const requestOwnerKeyRef = useRef<string | null>(requestOwnerKey);
  requestOwnerKeyRef.current = requestOwnerKey;

  const callResearchApi = useCallback(
    async (body: Record<string, unknown>, signal?: AbortSignal) => {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      throwIfAborted(signal);
      const response = await fetch('/api/research-assistant', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        signal,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error?.message || 'Request failed');
      }
      return payload;
    },
    [user]
  );

  const callAttuneApi = useCallback(
    async <T,>(
      path: string,
      body: Record<string, unknown> = {},
      signal?: AbortSignal
    ): Promise<T> => {
      if (!user || !projectId) throw new Error('Project is not ready');
      const token = await user.getIdToken();
      throwIfAborted(signal);
      const response = await fetch(path, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projectId, ...body }),
        signal,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error?.message || payload.error || 'Request failed');
      }
      return payload as T;
    },
    [projectId, user]
  );

  const loadApprovalPendingCount = useCallback(
    async (signal?: AbortSignal) => {
      const requestSequence = ++approvalCountRequestSequenceRef.current;
      if (!user || !projectId) {
        setApprovalPendingCount(0);
        return;
      }
      try {
        const [deliveryResult, backgroundResult] = await Promise.all([
          Promise.resolve(deliveryApprovalsEnabled
            ? callAttuneApi<{ total?: number; pendingCount?: number }>(
                '/api/attune/delivery-approvals/list',
                {
                  statuses: ['pending', 'executing', 'stale', 'reconciliation_required'],
                  limit: 1,
                },
                signal
              )
            : { total: 0, pendingCount: 0 }
          ),
          callAttuneApi<{ pendingCount: number }>('/api/attune/scan-runs/approval-queue', {}, signal)
            .catch(() => ({ pendingCount: 0 })),
        ]);
        if (signal?.aborted || requestSequence !== approvalCountRequestSequenceRef.current) {
          return;
        }
        const deliveryCount = Math.max(
          0,
          Number(deliveryResult.total ?? deliveryResult.pendingCount) || 0
        );
        const nextCount = deliveryCount + Math.max(0, Number(backgroundResult.pendingCount) || 0);
        setApprovalPendingCount(nextCount);
        publishWorkspaceApprovalPendingCount(projectId, nextCount);
      } catch (error) {
        if (!isAbortError(error) && requestSequence === approvalCountRequestSequenceRef.current) {
          setApprovalPendingCount(0);
        }
      }
    },
    [callAttuneApi, deliveryApprovalsEnabled, projectId, user]
  );

  useEffect(() => {
    const controller = new AbortController();
    let inFlight: Promise<void> | null = null;
    let refreshQueued = false;
    let lastRefreshStartedAt = 0;
    const refresh = (force = false) => {
      if (!force && document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (inFlight) {
        if (force) refreshQueued = true;
        return;
      }
      if (!force && now - lastRefreshStartedAt < APPROVAL_COUNT_MIN_REFRESH_GAP_MS) return;
      lastRefreshStartedAt = now;
      inFlight = loadApprovalPendingCount(controller.signal).finally(() => {
        inFlight = null;
        if (refreshQueued && !controller.signal.aborted) {
          refreshQueued = false;
          refresh(true);
        }
      });
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const refreshOnFocus = () => refresh();
    refresh(true);
    const interval = window.setInterval(() => refresh(), APPROVAL_COUNT_REFRESH_MS);
    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    const unsubscribe = subscribeDeliveryApprovalsChanged(changedProjectId => {
      if (changedProjectId === projectId) refresh(true);
    });
    return () => {
      approvalCountRequestSequenceRef.current += 1;
      controller.abort();
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      unsubscribe();
    };
  }, [loadApprovalPendingCount, projectId]);

  const loadSessions = useCallback(async () => {
    if (sessionsRetryTimerRef.current) {
      window.clearTimeout(sessionsRetryTimerRef.current);
      sessionsRetryTimerRef.current = null;
    }
    sessionsRequestRef.current?.controller.abort();
    if (!user || !projectId || !requestOwnerKey) {
      sessionsRequestRef.current = null;
      setSessions(current => (current.length > 0 ? [] : current));
      setSharedSessions(current => (current.length > 0 ? [] : current));
      setSharedSessionsCursor(null);
      setSharedSessionsHasMore(false);
      setSessionsLoading(false);
      setThreadOrganizationReady(false);
      setThreadOrganizationProjectId(null);
      threadOrganizationLoadedProjectRef.current = null;
      return;
    }

    const request: AppRailRequest = {
      id: ++requestSequenceRef.current,
      ownerKey: requestOwnerKey,
      controller: new AbortController(),
    };
    sessionsRequestRef.current = request;
    const isCurrentRequest = () =>
      sessionsRequestRef.current?.id === request.id &&
      requestOwnerKeyRef.current === request.ownerKey;

    setSessionsLoading(true);
    const includeThreadOrganization = threadOrganizationLoadedProjectRef.current !== projectId;
    try {
      const payload = await callResearchApi(
        {
          action: 'get_workspace_rail_bootstrap',
          projectId,
          limit: APP_RAIL_BATCH_SIZE,
          includeThreadOrganization,
        },
        request.controller.signal
      );
      if (!isCurrentRequest()) return;
      setSessions(Array.isArray(payload.sessions) ? payload.sessions : []);
      setSharedSessions(Array.isArray(payload.sharedSessions) ? payload.sharedSessions : []);
      setSharedSessionsCursor(
        typeof payload.sharedSessionsCursor === 'string' ? payload.sharedSessionsCursor : null
      );
      setSharedSessionsHasMore(payload.sharedSessionsHasMore === true);
      if (includeThreadOrganization && threadOrganizationLoadedProjectRef.current !== projectId) {
        skipOrganizationSaveRef.current = true;
        setThreadOrganization(createThreadOrganizationState(payload.threadOrganization));
        setOrganizationSessions(
          Array.isArray(payload.organizationSessions) ? payload.organizationSessions : []
        );
        setThreadOrganizationProjectId(projectId);
        setThreadOrganizationReady(true);
        threadOrganizationLoadedProjectRef.current = projectId;
      }
      sessionsRetryAttemptRef.current = 0;
    } catch (error) {
      if (!isCurrentRequest() || isAbortError(error)) return;
      if (includeThreadOrganization && threadOrganizationLoadedProjectRef.current !== projectId) {
        setThreadOrganizationReady(false);
        setThreadOrganizationProjectId(null);
      }
      if (sessionsRetryAttemptRef.current < APP_RAIL_MAX_RETRIES) {
        const retryDelay = APP_RAIL_RETRY_MS * 2 ** sessionsRetryAttemptRef.current;
        sessionsRetryAttemptRef.current += 1;
        sessionsRetryTimerRef.current = window.setTimeout(() => {
          sessionsRetryTimerRef.current = null;
          if (requestOwnerKeyRef.current === request.ownerKey) void loadSessions();
        }, retryDelay);
      }
    } finally {
      if (!isCurrentRequest()) return;
      sessionsRequestRef.current = null;
      setSessionsLoading(false);
    }
  }, [callResearchApi, projectId, requestOwnerKey, user]);

  const loadMoreSharedSessions = useCallback(async () => {
    if (sharedSessionsLoadingMore || !sharedSessionsHasMore || !sharedSessionsCursor || !projectId)
      return;
    setSharedSessionsLoadingMore(true);
    try {
      const payload = await callResearchApi({
        action: 'list_shared_sessions',
        projectId,
        limit: APP_RAIL_BATCH_SIZE,
        cursor: sharedSessionsCursor,
      });
      const nextSessions = Array.isArray(payload.sessions) ? payload.sessions : [];
      setSharedSessions(current => {
        const existingIds = new Set(current.map(session => session.id));
        return [
          ...current,
          ...nextSessions.filter((session: SessionSummary) => !existingIds.has(session.id)),
        ];
      });
      setSharedSessionsCursor(typeof payload.nextCursor === 'string' ? payload.nextCursor : null);
      setSharedSessionsHasMore(payload.hasMore === true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load shared threads');
    } finally {
      setSharedSessionsLoadingMore(false);
    }
  }, [
    callResearchApi,
    projectId,
    sharedSessionsCursor,
    sharedSessionsHasMore,
    sharedSessionsLoadingMore,
  ]);

  const loadBackgroundAgentSessions = useCallback(async () => {
    const includeSessionSummaries = !backgroundAgentsCollapsed;
    backgroundAgentsRequestRef.current?.controller.abort();
    if (!user || !projectId || !requestOwnerKey) {
      backgroundAgentsRequestRef.current = null;
      setBackgroundAgentSessions(current => (current.length > 0 ? [] : current));
      setBackgroundAgentSessionIdOrder(current => (current.length > 0 ? [] : current));
      setBackgroundAgentSessionIdsLoaded(false);
      setBackgroundAgentSessionsLoaded(false);
      return;
    }

    const request: AppRailRequest = {
      id: ++requestSequenceRef.current,
      ownerKey: requestOwnerKey,
      controller: new AbortController(),
    };
    backgroundAgentsRequestRef.current = request;
    const isCurrentRequest = () =>
      backgroundAgentsRequestRef.current?.id === request.id &&
      requestOwnerKeyRef.current === request.ownerKey;

    try {
      const automationPayload = await callAttuneApi<{
        latestRunSessions?: AppRailLatestRunSession[];
      }>(
        '/api/attune/scan-automations/list',
        { includeLatestRunSessions: true },
        request.controller.signal
      );
      const runEntries = Array.isArray(automationPayload.latestRunSessions)
        ? automationPayload.latestRunSessions
        : [];
      const sessionIds = Array.from(
        new Set(
          runEntries
            .sort((a, b) => getBackgroundAgentRunTimestamp(b) - getBackgroundAgentRunTimestamp(a))
            .map(entry => entry.session_id)
        )
      );
      if (!isCurrentRequest()) return;
      setBackgroundAgentSessionIdOrder(current =>
        current.length === sessionIds.length &&
        current.every((sessionId, index) => sessionId === sessionIds[index])
          ? current
          : sessionIds
      );
      setBackgroundAgentSessionIdsLoaded(true);
      setBackgroundAgentSessions(current =>
        current.filter(session => sessionIds.includes(session.id))
      );
      if (sessionIds.length === 0) {
        setBackgroundAgentSessionsLoaded(true);
        return;
      }
      if (!includeSessionSummaries) return;

      const sessionPayload = await callResearchApi(
        {
          action: 'list_sessions_by_ids',
          projectId,
          sessionIds,
        },
        request.controller.signal
      );
      if (!isCurrentRequest()) return;
      const sessionsById = new Map<string, SessionSummary>(
        (Array.isArray(sessionPayload.sessions) ? sessionPayload.sessions : []).map(
          (session: SessionSummary) => [session.id, session]
        )
      );
      setBackgroundAgentSessions(
        sessionIds
          .map(sessionId => sessionsById.get(sessionId))
          .filter(
            (session): session is SessionSummary =>
              session !== undefined && session.accessRole === 'owner'
          )
      );
      setBackgroundAgentSessionsLoaded(true);
    } catch (error) {
      if (!isCurrentRequest() || isAbortError(error)) return;
      setBackgroundAgentSessions(current => (current.length > 0 ? [] : current));
      setBackgroundAgentSessionIdOrder(current => (current.length > 0 ? [] : current));
      setBackgroundAgentSessionIdsLoaded(true);
      setBackgroundAgentSessionsLoaded(true);
    } finally {
      if (isCurrentRequest()) {
        backgroundAgentsRequestRef.current = null;
      }
    }
  }, [backgroundAgentsCollapsed, callAttuneApi, callResearchApi, projectId, requestOwnerKey, user]);

  useEffect(() => {
    if (!projectReady || projectLoading || !requestOwnerKey) {
      sessionsRequestRef.current?.controller.abort();
      sessionsRequestRef.current = null;
      setSessions(current => (current.length > 0 ? [] : current));
      setSharedSessions(current => (current.length > 0 ? [] : current));
      setSharedSessionsCursor(null);
      setSharedSessionsHasMore(false);
      setSessionsLoading(false);
      return;
    }
    sessionsRetryAttemptRef.current = 0;
    void loadSessions();
    return () => {
      const request = sessionsRequestRef.current;
      if (request?.ownerKey === requestOwnerKey) {
        request.controller.abort();
        sessionsRequestRef.current = null;
      }
      if (sessionsRetryTimerRef.current) {
        window.clearTimeout(sessionsRetryTimerRef.current);
        sessionsRetryTimerRef.current = null;
      }
      sessionsRetryAttemptRef.current = 0;
    };
  }, [loadSessions, projectLoading, projectReady, requestOwnerKey]);

  useEffect(() => {
    if (!projectId) return;

    return subscribeResearchSessionListChanged(change => {
      if (change.projectId !== projectId) return;

      if (change.type === 'upsert') {
        const organizationBootstrapPending =
          threadOrganizationLoadedProjectRef.current !== projectId;
        sessionsRequestRef.current?.controller.abort();
        sessionsRequestRef.current = null;
        setSessionsLoading(false);
        setSessions(current => upsertSessionInList(current, change.session));
        setOrganizationSessions(current =>
          current.some(session => session.id === change.session.id)
            ? upsertSessionInList(current, change.session)
            : current
        );
        setBackgroundAgentSessions(current =>
          current.some(session => session.id === change.session.id)
            ? upsertSessionInList(current, change.session)
            : current
        );
        setSessionsLoading(false);
        if (organizationBootstrapPending) void loadSessions();
        return;
      }

      if (change.type === 'remove') {
        const organizationBootstrapPending =
          threadOrganizationLoadedProjectRef.current !== projectId;
        sessionsRequestRef.current?.controller.abort();
        sessionsRequestRef.current = null;
        setSessionsLoading(false);
        setSessions(current => removeSessionFromList(current, change.sessionId));
        setOrganizationSessions(current => removeSessionFromList(current, change.sessionId));
        setBackgroundAgentSessions(current => removeSessionFromList(current, change.sessionId));
        setBackgroundAgentSessionIdOrder(current =>
          current.filter(sessionId => sessionId !== change.sessionId)
        );
        setSharedSessions(current => removeSessionFromList(current, change.sessionId));
        setSessionsLoading(false);
        if (organizationBootstrapPending) void loadSessions();
        return;
      }

      if (change.type === 'replace') {
        const organizationBootstrapPending =
          threadOrganizationLoadedProjectRef.current !== projectId;
        sessionsRequestRef.current?.controller.abort();
        sessionsRequestRef.current = null;
        setSessionsLoading(false);
        setSessions(current =>
          sortSessionsByUpdatedAt([
            ...change.sessions,
            ...current.filter(
              session => !change.sessions.some(updated => updated.id === session.id)
            ),
          ])
        );
        setSessionsLoading(false);
        if (organizationBootstrapPending) void loadSessions();
        return;
      }

      void loadSessions();
    });
  }, [loadSessions, projectId]);

  useEffect(() => {
    if (!projectReady || projectLoading || !requestOwnerKey) {
      backgroundAgentsRequestRef.current?.controller.abort();
      backgroundAgentsRequestRef.current = null;
      setBackgroundAgentSessions(current => (current.length > 0 ? [] : current));
      setBackgroundAgentSessionIdOrder(current => (current.length > 0 ? [] : current));
      setBackgroundAgentSessionIdsLoaded(false);
      setBackgroundAgentSessionsLoaded(false);
      return;
    }
    void loadBackgroundAgentSessions();
    return () => {
      const request = backgroundAgentsRequestRef.current;
      if (request?.ownerKey === requestOwnerKey) {
        request.controller.abort();
        backgroundAgentsRequestRef.current = null;
      }
    };
  }, [
    backgroundAgentsCollapsed,
    loadBackgroundAgentSessions,
    projectLoading,
    projectReady,
    requestOwnerKey,
  ]);

  useEffect(() => {
    setVisibleSessionCount(APP_RAIL_BATCH_SIZE);
    setVisibleBackgroundAgentCount(THREAD_FOLDER_BATCH_SIZE);
    setVisibleFolderThreadCounts({});
    setSharedWithMeCollapsed(false);
    setBackgroundAgentsCollapsed(true);
    setBackgroundAgentSessionIdOrder([]);
    setBackgroundAgentSessionIdsLoaded(false);
    setBackgroundAgentSessionsLoaded(false);
    threadOrganizationLoadedProjectRef.current = null;
  }, [projectId]);

  useEffect(() => {
    setDragTarget(null);
    if (projectReady && !projectLoading && user && projectId) return;
    skipOrganizationSaveRef.current = true;
    setThreadOrganization(createThreadOrganizationState());
    setOrganizationSessions([]);
    setThreadOrganizationReady(false);
    setThreadOrganizationProjectId(null);
    threadOrganizationLoadedProjectRef.current = null;
  }, [projectId, projectLoading, projectReady, user]);

  const threadOrganizationReadyForProject =
    threadOrganizationReady && threadOrganizationProjectId === projectId;

  useEffect(() => {
    if (!threadOrganizationReadyForProject || !user || !projectId) return;
    if (skipOrganizationSaveRef.current) {
      skipOrganizationSaveRef.current = false;
      return;
    }

    void callResearchApi({
      action: 'update_thread_organization',
      projectId,
      threadOrganization,
    }).catch(() => {
      toast.error('Failed to save thread organization');
    });
  }, [callResearchApi, projectId, threadOrganization, threadOrganizationReadyForProject, user]);

  const organizedThreadIds = useMemo(() => {
    const ids = new Set(threadOrganization.pinnedThreadIds);
    for (const threadId of Object.keys(threadOrganization.threadFolderIds)) {
      ids.add(threadId);
    }
    return ids;
  }, [threadOrganization.pinnedThreadIds, threadOrganization.threadFolderIds]);
  const mergedSessions = useMemo(
    () => mergeThreadOrganizationSessions(sessions, organizationSessions),
    [organizationSessions, sessions]
  );
  const backgroundAgentSessionIds = useMemo(
    () => new Set(backgroundAgentSessionIdOrder),
    [backgroundAgentSessionIdOrder]
  );
  const sharedSessionIds = useMemo(
    () => new Set(sharedSessions.map(session => session.id)),
    [sharedSessions]
  );
  const nonBackgroundSessions = useMemo(
    () => mergedSessions.filter(session => !backgroundAgentSessionIds.has(session.id)),
    [backgroundAgentSessionIds, mergedSessions]
  );
  const visibleSessions = useMemo(
    () =>
      nonBackgroundSessions.filter(
        (session, index) => index < visibleSessionCount || organizedThreadIds.has(session.id)
      ),
    [nonBackgroundSessions, organizedThreadIds, visibleSessionCount]
  );
  const threadView = useMemo(
    () => buildThreadOrganizationView(visibleSessions, threadOrganization),
    [threadOrganization, visibleSessions]
  );
  const pinnedThreadIds = useMemo(
    () => new Set(threadOrganization.pinnedThreadIds),
    [threadOrganization.pinnedThreadIds]
  );
  const hasMoreSessions = nonBackgroundSessions.some(
    (session, index) => index >= visibleSessionCount && !organizedThreadIds.has(session.id)
  );
  const threadOrganizationLoading = Boolean(
    projectReady && !projectLoading && user && projectId && !threadOrganizationReadyForProject
  );
  const backgroundAgentSessionIdsLoading = Boolean(
    projectReady && !projectLoading && user && projectId && !backgroundAgentSessionIdsLoaded
  );
  const threadListLoading =
    sessionsLoading || threadOrganizationLoading || backgroundAgentSessionIdsLoading;
  const backgroundAgentsActive = activeSessionId
    ? backgroundAgentSessionIds.has(activeSessionId)
    : false;
  const sharedWithMeActive = activeSessionId ? sharedSessionIds.has(activeSessionId) : false;
  const getThreadHref = useCallback((sessionId: string) => {
    return `/app/workspace?session=${encodeURIComponent(sessionId)}`;
  }, []);

  useEffect(() => {
    if (!activeSessionId) return;

    const activeBackgroundAgentIndex = backgroundAgentSessions.findIndex(
      session => session.id === activeSessionId
    );
    if (activeBackgroundAgentIndex >= THREAD_FOLDER_BATCH_SIZE) {
      setVisibleBackgroundAgentCount(current => Math.max(current, activeBackgroundAgentIndex + 1));
    }

    const activeFolder = threadView.folders.find(folder =>
      folder.threads.some(session => session.id === activeSessionId)
    );
    if (!activeFolder) return;

    const activeFolderThreadIndex = activeFolder.threads.findIndex(
      session => session.id === activeSessionId
    );
    if (activeFolderThreadIndex < THREAD_FOLDER_BATCH_SIZE) return;

    setVisibleFolderThreadCounts(current => {
      const currentCount = current[activeFolder.id] ?? THREAD_FOLDER_BATCH_SIZE;
      if (activeFolderThreadIndex < currentCount) return current;
      return {
        ...current,
        [activeFolder.id]: activeFolderThreadIndex + 1,
      };
    });
  }, [activeSessionId, backgroundAgentSessions, threadView.folders]);

  const handleCreateFolder = useCallback(() => {
    setNameDialog({ mode: 'folder' });
    setNameDialogValue('');
    setNameDialogFolderIcon(DEFAULT_THREAD_FOLDER_ICON);
  }, []);

  const closeNameDialog = useCallback(() => {
    if (nameDialogSaving) return;
    setNameDialog(null);
    setNameDialogValue('');
    setNameDialogFolderIcon(DEFAULT_THREAD_FOLDER_ICON);
  }, [nameDialogSaving]);

  const handleToggleThreadPin = useCallback((sessionId: string) => {
    setThreadOrganization(current => togglePinnedThread(current, sessionId));
  }, []);

  const handleToggleFolderCollapsed = useCallback((folder: OrganizedThreadFolder) => {
    setThreadOrganization(current =>
      setThreadFolderCollapsed(current, folder.id, !folder.collapsed)
    );
  }, []);

  const handleRenameThread = useCallback((session: SessionSummary) => {
    const currentTitle = getResearchSessionDisplayTitle(session);
    setNameDialog({ mode: 'rename', session });
    setNameDialogValue(session.name || currentTitle);
  }, []);

  const handleSaveNameDialog = useCallback(
    async (event?: FormEvent) => {
      event?.preventDefault();
      if (!nameDialog || nameDialogSaving) return;

      const normalizedName = nameDialogValue.trim();
      if (nameDialog.mode === 'folder') {
        if (!normalizedName) return;
        setThreadOrganization(current =>
          createThreadFolder(current, {
            id: createFolderId(),
            name: normalizedName,
            icon: nameDialogFolderIcon,
          })
        );
        setNameDialog(null);
        setNameDialogValue('');
        setNameDialogFolderIcon(DEFAULT_THREAD_FOLDER_ICON);
        return;
      }

      setNameDialogSaving(true);
      try {
        const payload = await callResearchApi({
          action: 'rename_session',
          sessionId: nameDialog.session.id,
          name: normalizedName || null,
        });
        const updatedName = typeof payload.name === 'string' ? payload.name : null;
        const renameSessionInList = (current: SessionSummary[]) =>
          current.map(item =>
            item.id === nameDialog.session.id ? { ...item, name: updatedName } : item
          );
        const updatedSession = { ...nameDialog.session, name: updatedName };
        setSessions(renameSessionInList);
        setOrganizationSessions(renameSessionInList);
        setBackgroundAgentSessions(renameSessionInList);
        if (nameDialog.session.origin?.type === 'slack') void slackThreads.refresh();
        notifyResearchSessionListChanged({
          type: 'upsert',
          projectId,
          session: updatedSession,
        });
        setNameDialog(null);
        setNameDialogValue('');
        toast.success('Thread renamed');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to rename thread');
      } finally {
        setNameDialogSaving(false);
      }
    },
    [
      callResearchApi,
      nameDialog,
      nameDialogFolderIcon,
      nameDialogSaving,
      nameDialogValue,
      projectId,
      slackThreads.refresh,
    ]
  );

  const handleArchiveThread = useCallback(
    async (session: SessionSummary) => {
      const submittedProjectId = projectId;
      if (!submittedProjectId) return;
      try {
        await callResearchApi({ action: 'archive_session', sessionId: session.id });
        if (activeProjectIdRef.current !== submittedProjectId) return;
        const removeSessionFromList = (current: SessionSummary[]) =>
          current.filter(item => item.id !== session.id);
        setSessions(removeSessionFromList);
        setOrganizationSessions(removeSessionFromList);
        setBackgroundAgentSessions(removeSessionFromList);
        notifyResearchSessionListChanged({
          type: 'remove',
          projectId: submittedProjectId,
          sessionId: session.id,
        });
        if (session.id === activeSessionId) {
          router.push('/app/workspace?new_chat=true');
        }
        toast.success('Thread archived');
      } catch (error) {
        if (activeProjectIdRef.current !== submittedProjectId) return;
        toast.error(error instanceof Error ? error.message : 'Failed to archive thread');
      }
    },
    [activeSessionId, callResearchApi, projectId, router]
  );

  const handleArchiveAllThreads = useCallback(async () => {
    const submittedProjectId = projectId;
    if (!submittedProjectId || archivingAllThreads) return;
    if (
      !window.confirm(
        'Archive all your threads in this project? You can restore them from Settings → Archived Threads.'
      )
    ) {
      return;
    }

    setArchivingAllThreads(true);
    try {
      const payload = await callResearchApi({
        action: 'archive_all_sessions',
        projectId: submittedProjectId,
      });
      if (activeProjectIdRef.current !== submittedProjectId) return;
      const archivedSessionIds = Array.isArray(payload.archivedSessionIds)
        ? payload.archivedSessionIds.filter(
            (sessionId: unknown): sessionId is string => typeof sessionId === 'string'
          )
        : [];
      const archivedSessionIdSet = new Set(archivedSessionIds);
      const removeArchivedSessions = (current: SessionSummary[]) =>
        current.filter(session => !archivedSessionIdSet.has(session.id));
      setSessions(removeArchivedSessions);
      setOrganizationSessions(removeArchivedSessions);
      setBackgroundAgentSessions(removeArchivedSessions);
      setBackgroundAgentSessionIdOrder(current =>
        current.filter(sessionId => !archivedSessionIdSet.has(sessionId))
      );
      for (const sessionId of archivedSessionIds) {
        notifyResearchSessionListChanged({
          type: 'remove',
          projectId: submittedProjectId,
          sessionId,
        });
      }
      if (activeSessionId && archivedSessionIdSet.has(activeSessionId)) {
        router.push('/app/workspace?new_chat=true');
      }
      const archivedCount = archivedSessionIds.length;
      toast.success(
        archivedCount === 1 ? '1 thread archived' : `${archivedCount} threads archived`
      );
    } catch (error) {
      if (activeProjectIdRef.current !== submittedProjectId) return;
      toast.error(error instanceof Error ? error.message : 'Failed to archive threads');
    } finally {
      if (activeProjectIdRef.current === submittedProjectId) setArchivingAllThreads(false);
    }
  }, [activeSessionId, archivingAllThreads, callResearchApi, projectId, router]);

  const handleExportMarkdown = useCallback(
    async (session: SessionSummary) => {
      if (!session.messageCount) {
        toast.error('No messages to export');
        return;
      }

      const title = getResearchSessionDisplayTitle(session);
      try {
        const payload = await callResearchApi({
          action: 'create_export',
          sessionId: session.id,
          title: `Research: ${title.substring(0, 50)}`,
          format: 'markdown',
        });
        if (typeof payload.export?.content === 'string') {
          downloadMarkdownFile(payload.export.title || title, payload.export.content);
        }
        toast.success('Markdown exported');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to export thread');
      }
    },
    [callResearchApi]
  );

  const handleRailSharingChange = useCallback(
    async (visibility: 'private' | 'workspace') => {
      if (!shareDialogSession) return;
      const payload = await callResearchApi({
        action: 'update_session_sharing',
        sessionId: shareDialogSession.id,
        visibility,
      });
      const updatedAt =
        typeof payload.sharing?.updatedAt === 'string'
          ? payload.sharing.updatedAt
          : shareDialogSession.updatedAt;
      const updatedSession: SessionSummary = {
        ...shareDialogSession,
        visibility,
        updatedAt,
      };
      const updateList = (current: SessionSummary[]) =>
        current.map(session => (session.id === updatedSession.id ? updatedSession : session));
      setSessions(updateList);
      setOrganizationSessions(updateList);
      setBackgroundAgentSessions(updateList);
      setShareDialogSession(updatedSession);
      notifyResearchSessionListChanged({
        type: 'upsert',
        projectId,
        session: updatedSession,
      });
    },
    [callResearchApi, projectId, shareDialogSession]
  );

  const handleThreadDragStart = useCallback(
    (event: DragEvent<HTMLDivElement>, sessionId: string) => {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData(THREAD_DRAG_DATA_TYPE, sessionId);
      event.dataTransfer.setData('text/plain', sessionId);
    },
    []
  );

  const getDraggedThreadId = useCallback((event: DragEvent<HTMLDivElement>): string | null => {
    return event.dataTransfer.getData(THREAD_DRAG_DATA_TYPE) || null;
  }, []);

  const hasDraggedThreadData = useCallback((event: DragEvent<HTMLDivElement>): boolean => {
    const types = Array.from(event.dataTransfer.types || []);
    return types.includes(THREAD_DRAG_DATA_TYPE);
  }, []);

  const handleThreadDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>, target: string) => {
      if (!hasDraggedThreadData(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      setDragTarget(target);
    },
    [hasDraggedThreadData]
  );

  const handleThreadDrop = useCallback(
    (event: DragEvent<HTMLDivElement>, folderId: string | null) => {
      const threadId = getDraggedThreadId(event);
      if (!threadId) return;
      event.preventDefault();
      setThreadOrganization(current => moveThreadToFolder(current, threadId, folderId));
      setDragTarget(null);
    },
    [getDraggedThreadId]
  );

  const handleDragLeave = useCallback(() => {
    setDragTarget(null);
  }, []);

  const renderThread = useCallback(
    (
      session: SessionSummary,
      folderId: string | null = null,
      options: RenderThreadOptions = {}
    ) => {
      const title = getResearchSessionDisplayTitle(session);
      const readOnly = options.readOnly === true || session.accessRole === 'workspace_viewer';
      const canDrag = !readOnly && options.draggable !== false;
      const canMoveFolders = !readOnly && options.allowFolderMoves !== false;
      return (
        <AppRailItem
          key={session.id}
          title={title}
          selected={session.id === activeSessionId}
          href={getThreadHref(session.id)}
          sourceHref={
            session.origin?.type === 'slack' ? session.origin.deepLink || undefined : undefined
          }
          {...(session.origin?.type === 'slack'
            ? {
                icon: appIcon(IconBrandSlack),
                rightLabel: session.origin.channelName ? `#${session.origin.channelName}` : 'Slack',
              }
            : {
                icon: options.icon,
                rightLabel: formatCodexRelativeTime(session.updatedAt),
              })}
          draggable={canDrag}
          pinned={pinnedThreadIds.has(session.id)}
          folderId={canMoveFolders ? folderId : null}
          onDragStart={canDrag ? event => handleThreadDragStart(event, session.id) : undefined}
          onTogglePin={readOnly ? undefined : () => handleToggleThreadPin(session.id)}
          onRename={readOnly ? undefined : () => void handleRenameThread(session)}
          onArchive={readOnly ? undefined : () => void handleArchiveThread(session)}
          onExportMarkdown={readOnly ? undefined : () => void handleExportMarkdown(session)}
          onShare={readOnly ? undefined : () => setShareDialogSession(session)}
          onMoveToLoose={
            canMoveFolders
              ? () => {
                  setThreadOrganization(current => moveThreadToFolder(current, session.id, null));
                }
              : undefined
          }
          folderMoveTargets={
            canMoveFolders
              ? threadOrganization.folders
                  .filter(folder => folder.id !== folderId)
                  .map(folder => ({ id: folder.id, name: folder.name }))
              : []
          }
          onMoveToFolder={
            canMoveFolders
              ? targetFolderId => {
                  setThreadOrganization(current =>
                    moveThreadToFolder(current, session.id, targetFolderId)
                  );
                }
              : undefined
          }
        />
      );
    },
    [
      activeSessionId,
      getThreadHref,
      handleArchiveThread,
      handleExportMarkdown,
      handleRenameThread,
      handleThreadDragStart,
      handleToggleThreadPin,
      pinnedThreadIds,
      threadOrganization.folders,
    ]
  );

  const nameDialogTitle = nameDialog?.mode === 'folder' ? 'New Folder' : 'Rename Thread';
  const nameDialogInputLabel = nameDialog?.mode === 'folder' ? 'Folder name' : 'Thread name';
  const visibleBackgroundAgentSessions = backgroundAgentSessions.slice(
    0,
    visibleBackgroundAgentCount
  );
  const handleRefreshRail = useCallback(async () => {
    if (railRefreshing || !projectReady || projectLoading || !requestOwnerKey || !projectId) return;

    setRailRefreshing(true);
    const ownerKey = requestOwnerKey;
    try {
      threadOrganizationLoadedProjectRef.current = null;
      setThreadOrganizationReady(false);
      await Promise.all([loadSessions(), loadBackgroundAgentSessions()]);
      if (requestOwnerKeyRef.current !== ownerKey) return;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to refresh left rail');
    } finally {
      setRailRefreshing(false);
    }
  }, [
    loadBackgroundAgentSessions,
    loadSessions,
    projectId,
    projectLoading,
    projectReady,
    railRefreshing,
    requestOwnerKey,
  ]);

  return (
    <>
      <AppRailFrame
        collapsed={railVisuallyCollapsed}
        floating={railCollapsed && railHovered}
        ready={railStateReady}
        onPointerEnter={handleRailPointerEnter}
        onPointerLeave={handleRailPointerLeave}
        onPointerCancel={closeRailHover}
      >
        <Flex h="58px" align="center" flexShrink={0} gap="2">
          <Flex
            data-rail-label=""
            inert={railVisuallyCollapsed}
            align="center"
            gap="2"
            flex="1"
            minW={0}
          >
            <Box flex="1" minW={0}>
              <CompactProjectSwitcher
                placement="rail"
                currentProject={currentProject}
                projects={projects}
                loading={projectLoading}
                canCreateProject={canCreateProject}
                onSelectProject={projectId => {
                  if (projects.some(project => project.id === projectId)) switchProject(projectId);
                }}
                onCreateProject={() => router.push(createProjectHref)}
              />
            </Box>
          </Flex>
          {!railVisuallyCollapsed && (
            <SidePanelToggleButton side="left" open label={railToggleLabel}
              controls="app-navigation-rail" onClick={toggleRailCollapsed} />
          )}
        </Flex>

        <HStack data-section="chatRailHeader" px="3" pt="3" pb="2" gap="1" flexShrink={0}>
          <Text minW={0} flex="1" fontSize="lg" fontWeight="600" letterSpacing="-0.025em">
            {
              {
                chat: 'Chats',
                work: 'Work',
                agents: 'Agents',
                site: 'Site',
                canvas: 'Canvas',
                data: 'Data Explorer',
                library: 'Library',
                tools: 'Plugins',
                settings: 'Settings',
              }[primarySection]
            }
          </Text>
          {primarySection === 'chat' ? (
            <AppRailThreadListMenu
              compact
              onCreateFolder={handleCreateFolder}
              onRefresh={() => void handleRefreshRail()}
              onArchiveAll={() => void handleArchiveAllThreads()}
              refreshing={railRefreshing || sessionsLoading}
              archivingAll={archivingAllThreads}
              archiveAllDisabled={
                threadListLoading ||
                (sessions.length === 0 &&
                  organizationSessions.length === 0 &&
                  backgroundAgentSessions.length === 0)
              }
            />
          ) : null}
        </HStack>
        <VStack
          display={primarySection === 'chat' ? 'flex' : 'none'}
          align="stretch"
          gap="0.5"
          flexShrink={0}
          mt="2.5"
        >
          <AppRailAction
            icon={appIcon(IconEdit)}
            label="New chat"
            href="/app/workspace?new_chat=true"
            active={isModeActive(pathname, searchParams, 'chat')}
            collapsed={railVisuallyCollapsed}
          />
          <AppRailSkillLauncher
            skills={railSkills.skills}
            loading={railSkills.loading}
            error={railSkills.error}
            enabled={railSkillLauncherEnabled}
            collapsed={railVisuallyCollapsed}
            onSelect={startNewTaskWithSkill}
          />
        </VStack>

        <Box
          data-section="threadListScrollRegion"
          flex="1"
          minH={0}
          overflowY="auto"
          overflowX="hidden"
          scrollbarGutter="stable"
        >
          <AppShellRailHeaderSlots />
          {primarySection === 'site' || primarySection === 'canvas' ? (
            <>
              <AppRailAction
                icon={appIcon(IconEdit)}
                label="New editing session"
                href={editorSessionHref(editorApp)}
                collapsed={railVisuallyCollapsed}
              />
              <AppRailNavGroup label="Recent sessions" collapsed={railVisuallyCollapsed}>
                {editorHistory.loading ? (
                  <Center py="4">
                    <InlineSpinner size="sm" />
                  </Center>
                ) : null}
                {editorHistory.error ? (
                  <Button size="sm" variant="ghost" onClick={editorHistory.retry}>
                    Retry loading sessions
                  </Button>
                ) : null}
                {editorHistory.sessions
                  .filter(entry => entry.editor === editorApp)
                  .map(entry => (
                    <AppRailItem
                      key={entry.sessionId}
                      title={entry.title}
                      href={editorSessionHref(editorApp, entry)}
                      selected={entry.sessionId === activeSessionId}
                      rightLabel={formatCodexRelativeTime(new Date(entry.visitedAt).toISOString())}
                    />
                  ))}
                {!editorHistory.loading &&
                !editorHistory.error &&
                !editorHistory.sessions.some(entry => entry.editor === editorApp) ? (
                  <Text px="3" py="2" fontSize="xs" color="ui.text.secondary">
                    No editing sessions yet.
                  </Text>
                ) : null}
              </AppRailNavGroup>
            </>
          ) : null}
          <VStack align="stretch" gap="0.5" mt="0.5">
            {primarySection === 'work' && !(pathname === '/app/workspace' && searchParams.get('mode') === 'tasks') && !/^\/app\/(objectives|key-results|plans|tasks)(\/|$)/.test(pathname) ? (
              <Box data-section="workFallbackNavigation">
              <AppRailNavGroup label="Work" collapsed={railVisuallyCollapsed}>
                <AppRailAction
                  icon={appIcon(IconLayoutKanban)}
                  label="Objectives"
                  href="/app/objectives"
                  active={isModeActive(pathname, searchParams, 'objectives')}
                  collapsed={railVisuallyCollapsed}
                />
                <AppRailAction
                  icon={appIcon(IconListCheck)}
                  label="Tasks"
                  href="/app/workspace?mode=tasks&tab=tasks"
                  active={
                    isModeActive(pathname, searchParams, 'tasks') &&
                    searchParams.get('tab') !== 'approvals'
                  }
                  collapsed={railVisuallyCollapsed}
                />
                <AppRailAction
                  icon={appIcon(IconShieldCheck)}
                  label="Approvals"
                  badge={
                    approvalPendingCount > 0
                      ? approvalPendingCount > 99
                        ? '99+'
                        : String(approvalPendingCount)
                      : undefined
                  }
                  href="/app/workspace?mode=tasks&tab=approvals"
                  active={isApprovalsActive(pathname, searchParams)}
                  collapsed={railVisuallyCollapsed}
                />
              </AppRailNavGroup>
              </Box>
            ) : null}
            {primarySection === 'agents' ? (
              <Box id={AGENTS_RAIL_SLOT_ID} display={railVisuallyCollapsed ? 'none' : 'block'} w="full" />
            ) : null}
            {primarySection === 'work' ? (
              <AppRailNavGroup label="Strategy" collapsed={railVisuallyCollapsed}>
                <AppRailAction
                  icon={appIcon(IconCompass)}
                  label="North Star"
                  href="/app/north-star"
                  active={isModeActive(pathname, searchParams, 'north-star')}
                  collapsed={railVisuallyCollapsed}
                />
                {aiCmoBetaEnabled ? (
                  <AppRailAction
                    icon={appIcon(IconGauge)}
                    label="Growth Plan"
                    badge="Beta"
                    href="/app/workspace?mode=growth-program"
                    active={isModeActive(pathname, searchParams, 'growth-program')}
                    collapsed={railVisuallyCollapsed}
                  />
                ) : null}
              </AppRailNavGroup>
            ) : null}
            {primarySection === 'library' ? (
              <AppRailNavGroup label="Library">
                <AppRailAction
                  icon={appIcon(IconFileText)}
                  label="Docs & Assets"
                  href="/app/docs"
                  active={isModeActive(pathname, searchParams, 'reports')}
                />
              </AppRailNavGroup>
            ) : null}
            {primarySection === 'tools' ? (
              <AppRailNavGroup label="Capabilities">
                <AppRailAction
                  icon={appIcon(IconPuzzle)}
                  label="Plugins"
                  href="/app/plugins"
                  active={isModeActive(pathname, searchParams, 'plugins')}
                />
                <AppRailAction
                  icon={appIcon(IconSparkles)}
                  label="Skills"
                  href="/app/skills"
                  active={isModeActive(pathname, searchParams, 'skills')}
                />
                <AppRailAction
                  icon={appIcon(IconServer)}
                  label="MCP"
                  href="/app/mcp"
                  active={isModeActive(pathname, searchParams, 'mcp')}
                />
              </AppRailNavGroup>
            ) : null}
            {primarySection === 'settings' ? (
              <AppRailAction
                icon={appIcon(IconSettings)}
                label="Settings"
                href="/app/settings"
                active
              />
            ) : null}
            {primarySection === 'data' ? <DataRailPanel /> : null}
          </VStack>

          <Box
            data-rail-label=""
            inert={railVisuallyCollapsed}
            display={primarySection === 'chat' ? 'flex' : 'none'}
            flexDirection="column"
            mt="3"
          >

            <Box>
              <WorkspaceSlackThreadsSection threads={slackThreads} scope={slackScope} workspaceId={slackWorkspaceId} activeSessionId={activeSessionId} onRename={handleRenameThread} />
              <VStack align="stretch" gap="0" minH="full">
                {threadListLoading ? (
                  <Center flex="1" minH="240px">
                    <InlineSpinner size="md" />
                  </Center>
                ) : (
                  <>

                    {sharedSessions.length > 0 ? (
                      <SharedWithMeThreadFolder
                        active={sharedWithMeActive}
                        collapsed={sharedWithMeCollapsed}
                        count={sharedSessions.length}
                        onToggle={() => setSharedWithMeCollapsed(current => !current)}
                      >
                        {sharedSessions.map(session =>
                          renderThread(session, null, {
                            draggable: false,
                            allowFolderMoves: false,
                            readOnly: true,
                          })
                        )}
                        {sharedSessionsHasMore ? (
                          <Button
                            data-section="sharedWithMeLoadMore"
                            size="xs"
                            variant="ghost"
                            justifyContent="center"
                            color="ui.text.tertiary"
                            loading={sharedSessionsLoadingMore}
                            onClick={() => void loadMoreSharedSessions()}
                          >
                            Load more
                          </Button>
                        ) : null}
                      </SharedWithMeThreadFolder>
                    ) : null}

                    {threadView.folders.map(folder => {
                      const target = `folder:${folder.id}`;
                      const visibleFolderThreadCount =
                        visibleFolderThreadCounts[folder.id] ?? THREAD_FOLDER_BATCH_SIZE;
                      return (
                        <AppRailThreadFolder
                          key={folder.id}
                          folder={folder}
                          active={dragTarget === target}
                          onToggle={() => handleToggleFolderCollapsed(folder)}
                          onDragOver={event => handleThreadDragOver(event, target)}
                          onDragLeave={handleDragLeave}
                          onDrop={event => handleThreadDrop(event, folder.id)}
                          onIconChange={icon => {
                            setThreadOrganization(current =>
                              setThreadFolderIcon(current, folder.id, icon)
                            );
                          }}
                        >
                          {folder.threads
                            .slice(0, visibleFolderThreadCount)
                            .map(session => renderThread(session, folder.id))}
                          {folder.threads.length > visibleFolderThreadCount ? (
                            <Button
                              data-section="threadFolderLoadMore"
                              size="xs"
                              variant="ghost"
                              justifyContent="center"
                              color="ui.text.tertiary"
                              onClick={() =>
                                setVisibleFolderThreadCounts(current => ({
                                  ...current,
                                  [folder.id]: Math.min(
                                    visibleFolderThreadCount + THREAD_FOLDER_BATCH_SIZE,
                                    folder.threads.length
                                  ),
                                }))
                              }
                            >
                              Load more
                            </Button>
                          ) : null}
                        </AppRailThreadFolder>
                      );
                    })}

                    {threadView.pinned.length > 0 ? (
                      <Box data-section="threadPinnedGroup" mt="1.5">
                        <VStack align="stretch" gap="0">
                          {threadView.pinned.map(session =>
                            renderThread(session, null, { icon: appIcon(IconPinned) })
                          )}
                        </VStack>
                      </Box>
                    ) : null}

                    <Box
                      data-section="threadLooseDropZone"
                      mt={threadView.pinned.length > 0 ? '0.5' : '1.5'}
                      borderRadius="8px"
                      bg={dragTarget === 'loose' ? 'ui.surface.selected' : 'transparent'}
                      minH={threadView.loose.length > 0 ? undefined : '2'}
                      onDragOver={event => handleThreadDragOver(event, 'loose')}
                      onDragLeave={handleDragLeave}
                      onDrop={event => handleThreadDrop(event, null)}
                    >
                      {threadView.loose.map(session => renderThread(session))}
                    </Box>

                    {hasMoreSessions ? (
                      <Button
                        size="xs"
                        variant="ghost"
                        justifyContent="center"
                        onClick={() =>
                          setVisibleSessionCount(count =>
                            Math.min(count + APP_RAIL_BATCH_SIZE, nonBackgroundSessions.length)
                          )
                        }
                      >
                        Load more sessions
                      </Button>
                    ) : null}
                  </>
                )}
              </VStack>
            </Box>
          </Box>
        </Box>
      </AppRailFrame>

      <Drawer.Root
        open={mobileThreadsOpen}
        onOpenChange={event => setMobileThreadsOpen(event.open)}
        placement="bottom"
      >
        <Portal>
          <Drawer.Backdrop
            display={{ base: 'block', lg: 'none' }}
            bg="ui.overlay.backdrop"
            backdropFilter="blur(4px)"
          />
          <Drawer.Positioner display={{ base: 'flex', lg: 'none' }}>
            <Drawer.Content
              layerStyle="drawerSurface"
              borderTopRadius="glassCard"
              borderBottomRadius="0"
              maxH="82dvh"
              overflow="hidden"
            >
              <Drawer.Header layerStyle="drawerHeader" px="4" py="3" position="relative">
                <Drawer.Title fontSize="md" fontWeight="700" color="ui.text.primary">
                  Threads
                </Drawer.Title>
                <Drawer.Description srOnly>Browse and manage workspace threads.</Drawer.Description>
                <Drawer.CloseTrigger asChild position="absolute" top="2.5" right="3">
                  <CloseButton size="sm" />
                </Drawer.CloseTrigger>
              </Drawer.Header>

              <Drawer.Body
                bg="ui.surface.drawer"
                px="3"
                pt="2"
                pb="4"
                display="flex"
                flexDirection="column"
                minH={0}
                overflow="hidden"
              >
                <AppRailThreadListMenu
                  onCreateFolder={handleCreateFolder}
                  onRefresh={() => void handleRefreshRail()}
                  onArchiveAll={() => void handleArchiveAllThreads()}
                  refreshing={railRefreshing || sessionsLoading}
                  archivingAll={archivingAllThreads}
                  archiveAllDisabled={
                    threadListLoading ||
                    (sessions.length === 0 &&
                      organizationSessions.length === 0 &&
                      backgroundAgentSessions.length === 0)
                  }
                />
                <Box
                  data-section="mobileThreadListScrollRegion"
                  flex="1"
                  minH={0}
                  overflowY="auto"
                  overflowX="hidden"
                  overscrollBehavior="contain"
                >
                  <WorkspaceSlackThreadsSection threads={slackThreads} scope={slackScope} workspaceId={slackWorkspaceId} activeSessionId={activeSessionId} onRename={handleRenameThread} />
                  <VStack align="stretch" gap="0" minH="full">
                    {threadListLoading ? (
                      <Center minH="240px">
                        <InlineSpinner size="md" />
                      </Center>
                    ) : (
                      <>

                        {sharedSessions.length > 0 ? (
                          <SharedWithMeThreadFolder
                            active={sharedWithMeActive}
                            collapsed={sharedWithMeCollapsed}
                            count={sharedSessions.length}
                            onToggle={() => setSharedWithMeCollapsed(current => !current)}
                          >
                            {sharedSessions.map(session =>
                              renderThread(session, null, {
                                draggable: false,
                                allowFolderMoves: false,
                                readOnly: true,
                              })
                            )}
                            {sharedSessionsHasMore ? (
                              <Button
                                data-section="mobileSharedWithMeLoadMore"
                                size="sm"
                                variant="ghost"
                                justifyContent="center"
                                color="ui.text.tertiary"
                                loading={sharedSessionsLoadingMore}
                                onClick={() => void loadMoreSharedSessions()}
                              >
                                Load more
                              </Button>
                            ) : null}
                          </SharedWithMeThreadFolder>
                        ) : null}

                        {threadView.folders.map(folder => (
                          <AppRailThreadFolder
                            key={folder.id}
                            folder={folder}
                            onToggle={() => handleToggleFolderCollapsed(folder)}
                            onDragOver={event => handleThreadDragOver(event, `folder:${folder.id}`)}
                            onDragLeave={handleDragLeave}
                            onDrop={event => handleThreadDrop(event, folder.id)}
                            onIconChange={icon => {
                              setThreadOrganization(current =>
                                setThreadFolderIcon(current, folder.id, icon)
                              );
                            }}
                          >
                            {folder.threads.map(session =>
                              renderThread(session, folder.id, { draggable: false })
                            )}
                          </AppRailThreadFolder>
                        ))}

                        {threadView.pinned.length > 0 ? (
                          <Box data-section="mobileThreadPinnedGroup" mt="1.5">
                            <VStack align="stretch" gap="0">
                              {threadView.pinned.map(session =>
                                renderThread(session, null, {
                                  draggable: false,
                                  icon: appIcon(IconPinned),
                                })
                              )}
                            </VStack>
                          </Box>
                        ) : null}

                        <Box mt={threadView.pinned.length > 0 ? '0.5' : '1.5'}>
                          {threadView.loose.map(session =>
                            renderThread(session, null, { draggable: false })
                          )}
                        </Box>

                        {hasMoreSessions ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            justifyContent="center"
                            onClick={() =>
                              setVisibleSessionCount(count =>
                                Math.min(count + APP_RAIL_BATCH_SIZE, nonBackgroundSessions.length)
                              )
                            }
                          >
                            Load more threads
                          </Button>
                        ) : null}
                      </>
                    )}
                  </VStack>
                </Box>
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>

      <Dialog.Root
        open={Boolean(nameDialog)}
        onOpenChange={event => {
          if (!event.open) closeNameDialog();
        }}
        placement="center"
      >
        <Portal>
          <Dialog.Backdrop bg="ui.overlay.backdrop" backdropFilter="blur(4px)" />
          <Dialog.Positioner>
            <Dialog.Content
              layerStyle="surfacePopover"
              maxW="360px"
              w="calc(100vw - 32px)"
              p="0"
              aria-busy={nameDialogSaving || undefined}
            >
              <Dialog.Header p="4" borderBottomWidth="1px" borderColor="ui.border.subtle">
                <Dialog.Title fontSize="sm" fontWeight="800" color="ui.text.primary">
                  {nameDialogTitle}
                </Dialog.Title>
                <Dialog.Description srOnly>
                  {nameDialog?.mode === 'folder'
                    ? 'Choose a folder icon and enter a name for the new folder.'
                    : 'Enter a new name for this thread.'}
                </Dialog.Description>
              </Dialog.Header>
              <form onSubmit={handleSaveNameDialog}>
                <Dialog.Body p="4">
                  {nameDialog?.mode === 'folder' ? (
                    <Box mb="3">
                      <Text fontSize="0.75rem" fontWeight="650" color="ui.text.tertiary" mb="2">
                        Folder icon
                      </Text>
                      <Box
                        data-section="folderEmojiPicker"
                        display="grid"
                        gridTemplateColumns="repeat(6, minmax(0, 1fr))"
                        gap="1"
                      >
                        {THREAD_FOLDER_EMOJI_OPTIONS.map(icon => (
                          <Button
                            key={icon}
                            type="button"
                            aria-label={`Use ${icon} folder icon`}
                            aria-pressed={nameDialogFolderIcon === icon}
                            size="sm"
                            variant="ghost"
                            minW="0"
                            h="8"
                            px="0"
                            borderRadius="8px"
                            bg={
                              nameDialogFolderIcon === icon ? 'ui.surface.selected' : 'transparent'
                            }
                            fontSize="1rem"
                            onClick={() => setNameDialogFolderIcon(icon)}
                          >
                            {icon}
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  ) : null}
                  <Input
                    aria-label={nameDialogInputLabel}
                    name="threadName"
                    autoComplete="off"
                    value={nameDialogValue}
                    onChange={event => setNameDialogValue(event.target.value)}
                    placeholder={`${nameDialogInputLabel}…`}
                    bg="ui.surface.input"
                    borderColor="ui.border.subtle"
                    color="ui.text.primary"
                    disabled={nameDialogSaving}
                  />
                </Dialog.Body>
                <Dialog.Footer p="4" pt="0" gap="2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={closeNameDialog}
                    disabled={nameDialogSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    disabled={nameDialogSaving || !nameDialogValue.trim()}
                    loading={nameDialogSaving}
                  >
                    Save
                  </Button>
                </Dialog.Footer>
              </form>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
      {shareDialogSession ? (
        <SessionShareDialog
          open
          sessionId={shareDialogSession.id}
          visibility={shareDialogSession.visibility}
          onOpenChange={open => {
            if (!open) setShareDialogSession(null);
          }}
          onVisibilityChange={handleRailSharingChange}
        />
      ) : null}
    </>
  );
}
